import React, { useState, useCallback, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Linking, Platform, TextInput, Modal, GestureResponderEvent } from 'react-native';
import Svg, { Polyline, Line, Text as SvgText, Rect, Circle } from 'react-native-svg';
import { useFocusEffect, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../src/api/client';
import { openExternal } from '../../src/utils/openExternal';
import { useToast } from '../../src/components/Toast';
import Button from '../../src/components/Button';
import Picker from '../../src/components/Picker';
import DateTimePicker from '../../src/components/DateTimePicker';
import InlineConfirm from '../../src/components/InlineConfirm';
import RefundDialog from '../../src/components/RefundDialog';
import Card from '../../src/components/Card';
import { useTheme } from '../../src/context/ThemeContext';
import { useNetwork } from '../../src/context/NetworkContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { rs, fs } from '../../src/utils/responsive';
import type { ColorTheme } from '../../src/utils/theme';
import ContentContainer from '../../src/components/ContentContainer';
import HardwareHealthCard from '../../src/components/HardwareHealthCard';
import ExpensesBox from '../../src/components/ExpensesBox';
import { formatDateTime } from '../../src/utils/timezone';
import { titleCase, formatPhone } from '../../src/utils/format';

// ───────── Types ─────────

interface DashboardData {
  today: { totalCharged: number; count: number };
  thisWeek: { totalCharged: number; count: number };
  thisMonth: { totalCharged: number; count: number };
  lastMonth: { totalCharged: number; count: number };
  yearToDate: { totalCharged: number; count: number };
  locationStats: any[];
  recentTransactions: any[];
  activeSessions: number;
  avgRating: number | null;
  ratingCount: number;
  /** Fleet washes recorded but not yet paid (count + total retail value in cents). */
  fleetPending?: { count: number; amount: number };
}
interface MrrData { mrr: number; activeCount: number; washesThisMonth: number; automaticWashesThisMonth?: number; selfServeSessionsThisMonth?: number; }
interface StripeStatus { onboarded: boolean; accountId: string | null; }
interface Balance { available: { amount: number; currency: string }[]; pending: { amount: number; currency: string }[]; }
interface Payout { id: string; amount: number; currency: string; status: string; arrivalDate: number; created: number; }
interface LocationRow { id: string; name: string; latitude: number | null; longitude: number | null; }
interface ProblemReport {
  id: string; transactionId: string; category: string; categoryLabel: string;
  description: string; contactPreference: string; status: string;
  reporterName: string | null; reporterEmail: string | null; reporterPhone: string | null;
  locationName: string | null; bayName: string | null;
  txType: string | null; txAmountCents: number | null;
  refundedAmountCents: number | null;
  resolvedAt: string | null; createdAt: string;
}

interface Transaction {
  id: string; type: string; tierName: string | null; durationMinutes: number | null;
  amount: number; refundedAmount?: number; platformFee: number; ownerPayout: number; status: string;
  stripeFee?: number | null;
  /** True when stripeFee is a 2.9% + 30¢ estimate (Stripe hasn't reported the actual fee yet). */
  stripeFeeEstimated?: boolean;
  stripePaymentIntentId: string | null; createdAt: string;
  locationName: string | null; bayName: string | null; customerName: string | null;
  customerEmail: string | null; customerPhone: string | null;
  paymentBrand: string | null; paymentLast4: string | null;
  fleetCompanyName: string | null; rating: number | null;
  /** Applied fleet volume discount % (fleet-wash rows only). */
  fleetDiscountPercent?: number | null;
  /** Fleet account id — used to call the fleet refund endpoint. */
  fleetAccountId?: string | null;
  /** True once the fleet wash is on a generated invoice (refund blocked). */
  fleetInvoiced?: boolean;
  /** Prepaid wash credit applied to this wash (cents) — funds otherwise-$0 washes. */
  creditUsedCents?: number;
  /** True when a problem report exists for this wash. */
  reported?: boolean;
  /** Session rows: funding kind ('card' | 'coin' | 'bundle' | ...). Bundle-funded
   *  vacuum sessions restore minutes instead of refunding dollars (1.4 P01). */
  paymentMethod?: string | null;
  /** Subscription-wash rows: 'automatic' | 'self_serve' | 'vacuum'. */
  bayType?: string | null;
}
interface RevenueRow { bucket: string; gross: number; fees: number; net: number; count: number; }
interface CompareData {
  period: string;
  current: { from: string; to: string; gross: number; net: number; count: number };
  prior: { from: string; to: string; gross: number; net: number; count: number };
  deltaPct: { gross: number | null; net: number | null; count: number | null };
}
interface ByBayRow {
  bayId: string; bayName: string; bayType: string; locationId: string; locationName: string;
  gross: number; fees: number; net: number; count: number; avgTicket: number; lastTxAt: string | null;
}
interface HeatmapCell { dow: number; hour: number; gross: number; count: number; }
interface WeatherRow { locationId: string; date: string; tempHighF: number | null; tempLowF: number | null; tempAvgF: number | null; precipInches: number; conditions: string | null; isRainyDay: boolean; }
interface WeatherToday {
  locationId: string;
  forecast: {
    properties?: {
      periods?: {
        startTime: string;
        temperature: number;
        temperatureUnit: string;
        shortForecast: string;
        probabilityOfPrecipitation?: { value: number | null };
      }[];
    };
  };
}

type RangePreset = 'today' | '7d' | '30d' | 'mtd' | 'ytd' | 'lastMonth' | 'custom';
type BaySortKey = 'bayName' | 'locationName' | 'count' | 'gross' | 'avgTicket' | 'lastTxAt';
type SortDir = 'asc' | 'desc';

// ───────── Date helpers ─────────

const dayMs = 86_400_000;
const fmtDate = (d: Date) => d.toISOString().slice(0, 10);

function rangeForPreset(preset: RangePreset, customFrom?: string, customTo?: string): { from: string; to: string; label: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today': return { from: fmtDate(today), to: fmtDate(now), label: 'Today' };
    case '7d': return { from: fmtDate(new Date(today.getTime() - 6 * dayMs)), to: fmtDate(now), label: 'Last 7 days' };
    case '30d': return { from: fmtDate(new Date(today.getTime() - 29 * dayMs)), to: fmtDate(now), label: 'Last 30 days' };
    case 'mtd': return { from: fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)), to: fmtDate(now), label: 'Month to date' };
    case 'ytd': return { from: fmtDate(new Date(now.getFullYear(), 0, 1)), to: fmtDate(now), label: 'Year to date' };
    case 'lastMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: fmtDate(start), to: fmtDate(end), label: 'Last month' };
    }
    case 'custom': return { from: customFrom || fmtDate(today), to: customTo || fmtDate(now), label: 'Custom range' };
  }
}

function applyAlpha(hex: string, a: number): string {
  const m = hex.match(/^#?([0-9a-fA-F]{6})$/);
  if (!m) return hex;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

// Per-location color palette (cycles for > N locations).
const LOC_COLORS = ['#3a8a8a', '#a86b2a', '#5a7d3a', '#7a4c8e', '#cc6643', '#4a6fa8'];

/**
 * Map an NWS short forecast string + precipitation to a 0..5 severity scale.
 * 0 = clear/sunny, 5 = thunderstorm. Used to draw weather lines under the
 * revenue trend so owners can see "this day was rainy".
 */
function weatherSeverity(conditions: string | null, precipInches: number): number {
  const c = (conditions ?? '').toLowerCase();
  if (c.includes('thunder') || c.includes('storm') || c.includes('lightning')) return 5;
  if (c.includes('heavy rain') || c.includes('heavy snow') || precipInches >= 0.5) return 4;
  if (c.includes('rain') || c.includes('shower') || c.includes('snow') || precipInches >= 0.1) return 3;
  if (c.includes('drizzle') || c.includes('overcast') || c.includes('mostly cloudy')) return 2;
  if (c.includes('cloud') || c.includes('partly')) return 1;
  return 0; // Sunny / Clear / Fair
}

const SEVERITY_LABELS = ['Sunny', 'Partly cloudy', 'Cloudy', 'Light rain', 'Heavy rain', 'Thunderstorm'];

// ───────── Chart with tooltip ─────────

interface WeatherLine { locationId: string; label: string; color: string; byDate: Record<string, { severity: number; conditions: string | null; precipInches: number }>; }

interface RevenueSeries { locationId: string; label: string; color: string; rows: RevenueRow[]; }

interface LineChartProps {
  revenue: RevenueRow[];
  /** Optional per-location series. When present they render alongside the combined "All" line. */
  revenueByLocation?: RevenueSeries[];
  weatherLines: WeatherLine[];
  /** UTC instant for the start of the range (the backend bucket strings sit in this tz). */
  fromTs: number;
  /** UTC instant for the end of the range (exclusive). */
  toTs: number;
  /** 'hour' | 'day' | 'month' — defaults 'day'. */
  granularity: 'hour' | 'day' | 'month';
  /** IANA owner timezone for formatting bucket labels. */
  timezone: string;
  height?: number;
  axisColor: string;
  labelColor: string;
  bg: string;
  revColor: string;
  width?: number;
}

/** Format a UTC instant as a wall-clock bucket string in `tz` matching the backend's to_char output. */
function formatBucket(d: Date, tz: string, granularity: 'hour' | 'day' | 'month'): string {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) parts[p.type] = p.value;
  const y = parts.year, m = parts.month, day = parts.day;
  const hh = parts.hour === '24' ? '00' : parts.hour;
  if (granularity === 'hour') return `${y}-${m}-${day}T${hh}:00:00`;
  if (granularity === 'month') return `${y}-${m}-01`;
  return `${y}-${m}-${day}`;
}

/** Generate the complete bucket axis between fromTs and toTs in tz local time. */
function buildBucketAxis(fromTs: number, toTs: number, granularity: 'hour' | 'day' | 'month', tz: string): string[] {
  const stepMs = granularity === 'hour' ? 3_600_000 : 86_400_000;
  const out: string[] = [];
  const seen = new Set<string>();
  // Start one step before to absorb DST edge, then bound by [fromTs, toTs).
  for (let t = fromTs; t < toTs; t += stepMs) {
    const b = formatBucket(new Date(t), tz, granularity);
    if (!seen.has(b)) { seen.add(b); out.push(b); }
  }
  return out;
}

/**
 * Two-band chart: revenue on the top band (~70% of height), per-location
 * weather severity (0=sunny → 5=thunderstorm) on the bottom band (~30%).
 * Revenue band shows a combined "All" line plus one line per location when
 * `revenueByLocation` is provided. Weather has one line per location.
 */
function RevenueLineChart({ revenue, revenueByLocation = [], weatherLines, fromTs, toTs, granularity, timezone, height = 240, axisColor, labelColor, bg, revColor, width = 600 }: LineChartProps) {
  const padL = 42, padR = 56, padT = 14, padB = 22;
  const innerW = width - padL - padR;
  const revBandH = Math.round((height - padT - padB) * 0.68);
  const wxBandH = (height - padT - padB) - revBandH - 8; // small gap

  // Full bucket axis covering the whole range — so a single-data-point day
  // shows up as a dot at the right time of day instead of an empty chart.
  const buckets = buildBucketAxis(fromTs, toTs, granularity, timezone);
  const bucketIdx: Record<string, number> = {};
  buckets.forEach((b, i) => { bucketIdx[b] = i; });

  if (revenue.length === 0 && revenueByLocation.every(s => s.rows.length === 0) && weatherLines.every(w => Object.keys(w.byDate).length === 0)) {
    return (
      <View style={{ height, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ color: labelColor, fontSize: 13 }}>No data in this range</Text>
      </View>
    );
  }

  const maxGross = Math.max(
    1,
    ...revenue.map(r => r.gross),
    ...revenueByLocation.flatMap(s => s.rows.map(r => r.gross)),
  );
  const stepX = buckets.length > 1 ? innerW / (buckets.length - 1) : 0;
  const x = (b: string) => padL + (bucketIdx[b] ?? 0) * stepX;

  // Revenue band: top of chart (padT) to padT+revBandH
  const yRev = (g: number) => padT + revBandH - (g / maxGross) * revBandH;
  // Weather band: just below revenue. Higher severity sits at the TOP of the
  // band (matching the "storm" axis label); 0=sunny sits at the bottom.
  const wxTop = padT + revBandH + 8;
  const yWx = (severity: number) => wxTop + (1 - severity / 5) * wxBandH;

  const revTicks = [0, maxGross / 2, maxGross];

  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const containerRef = useRef<View>(null);

  const webPointerHandlers = Platform.OS === 'web' ? {
    onMouseMove: (e: any) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const scaleX = width / rect.width;
      const localX = (e.clientX - rect.left) * scaleX;
      const idx = Math.round((localX - padL) / stepX);
      if (idx >= 0 && idx < buckets.length) setHoverIdx(idx); else setHoverIdx(null);
    },
    onMouseLeave: () => setHoverIdx(null),
  } : {};

  const nativeTouchHandlers = Platform.OS !== 'web' ? {
    onStartShouldSetResponder: () => true,
    onMoveShouldSetResponder: () => true,
    onResponderMove: (e: GestureResponderEvent) => {
      const lx = e.nativeEvent.locationX;
      containerRef.current?.measure((_x, _y, w) => {
        if (!w) return;
        const scaleX = width / w;
        const idx = Math.round((lx * scaleX - padL) / stepX);
        if (idx >= 0 && idx < buckets.length) setHoverIdx(idx); else setHoverIdx(null);
      });
    },
    onResponderRelease: () => setTimeout(() => setHoverIdx(null), 1500),
  } : {};

  const tooltipLines: string[] = [];
  if (hoverIdx != null) {
    const b = buckets[hoverIdx];
    tooltipLines.push(b);
    const r = revenue.find(rr => rr.bucket === b);
    if (r) tooltipLines.push(`All: $${(r.gross / 100).toFixed(0)} · ${r.count} washes`);
    for (const s of revenueByLocation) {
      const lr = s.rows.find(rr => rr.bucket === b);
      if (lr) tooltipLines.push(`${s.label}: $${(lr.gross / 100).toFixed(0)} · ${lr.count} washes`);
    }
    for (const wl of weatherLines) {
      const w = wl.byDate[b];
      if (w) {
        const label = SEVERITY_LABELS[Math.min(5, Math.max(0, w.severity))] ?? '';
        tooltipLines.push(`${wl.label}: ${label}${w.precipInches > 0 ? ` · ${w.precipInches.toFixed(2)}″` : ''}`);
      }
    }
  }

  return (
    <View ref={containerRef} style={{ width: '100%' }} {...webPointerHandlers} {...nativeTouchHandlers}>
      <Svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        {/* Revenue grid + ticks */}
        {revTicks.map((t, i) => {
          const ty = yRev(t);
          return (
            <React.Fragment key={`rev-${i}`}>
              <Line x1={padL} y1={ty} x2={width - padR} y2={ty} stroke={axisColor} strokeWidth={0.5} opacity={0.4} />
              <SvgText x={4} y={ty + 4} fontSize={9} fill={labelColor}>${Math.round(t / 100)}</SvgText>
            </React.Fragment>
          );
        })}
        {/* Per-location revenue lines — drawn first so the "All" line sits on top. */}
        {revenueByLocation.map(s => {
          if (s.rows.length === 0) return null;
          const byBucket: Record<string, number> = {};
          for (const r of s.rows) byBucket[r.bucket] = r.gross;
          const pts = buckets.map(b => `${x(b)},${yRev(byBucket[b] ?? 0)}`).join(' ');
          return (
            <React.Fragment key={`loc-${s.locationId}`}>
              <Polyline points={pts} fill="none" stroke={s.color} strokeWidth={1.5} opacity={0.85} />
              {s.rows.map(r => bucketIdx[r.bucket] != null ? (
                <Circle key={`loc-${s.locationId}-${r.bucket}`} cx={x(r.bucket)} cy={yRev(r.gross)} r={2} fill={s.color} opacity={0.9} />
              ) : null)}
            </React.Fragment>
          );
        })}
        {/* "All" revenue line — drawn across the full bucket axis with $0 for empty buckets,
            so a single-point day still shows a proper hourly baseline + spike. */}
        {revenue.length > 0 && (() => {
          const byBucket: Record<string, number> = {};
          for (const r of revenue) byBucket[r.bucket] = r.gross;
          const pts = buckets.map(b => `${x(b)},${yRev(byBucket[b] ?? 0)}`).join(' ');
          return <Polyline points={pts} fill="none" stroke={revColor} strokeWidth={2.25} />;
        })()}
        {/* Dot on every bucket that actually has revenue — single points stay visible. */}
        {revenue.map(r => bucketIdx[r.bucket] != null ? (
          <Circle key={`pt-${r.bucket}`} cx={x(r.bucket)} cy={yRev(r.gross)} r={2.5} fill={revColor} />
        ) : null)}

        {/* Weather band divider */}
        <Line x1={padL} y1={wxTop - 4} x2={width - padR} y2={wxTop - 4} stroke={axisColor} strokeWidth={0.5} opacity={0.4} strokeDasharray="3,3" />
        {/* Weather band labels on right side */}
        <SvgText x={width - padR + 4} y={wxTop + 4} fontSize={8} fill={labelColor}>storm</SvgText>
        <SvgText x={width - padR + 4} y={wxTop + wxBandH + 2} fontSize={8} fill={labelColor}>sun</SvgText>

        {/* Per-location weather lines + dots */}
        {weatherLines.map(wl => {
          const pts: { x: number; y: number; bucket: string }[] = [];
          for (const b of buckets) {
            const w = wl.byDate[b];
            if (w) pts.push({ x: x(b), y: yWx(w.severity), bucket: b });
          }
          if (pts.length === 0) return null;
          return (
            <React.Fragment key={wl.locationId}>
              {pts.length > 1 && (
                <Polyline
                  points={pts.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="none" stroke={wl.color} strokeWidth={1.5} strokeDasharray="2,2" opacity={0.85}
                />
              )}
              {pts.map(p => (
                <Circle key={`${wl.locationId}-${p.bucket}`} cx={p.x} cy={p.y} r={2} fill={wl.color} opacity={0.9} />
              ))}
            </React.Fragment>
          );
        })}

        {/* Hover crosshair + dots */}
        {hoverIdx != null && (
          <>
            <Line x1={x(buckets[hoverIdx])} y1={padT} x2={x(buckets[hoverIdx])} y2={padT + revBandH + 8 + wxBandH} stroke={labelColor} strokeWidth={0.5} opacity={0.5} />
            {(() => {
              const r = revenue.find(rr => rr.bucket === buckets[hoverIdx]);
              if (!r) return null;
              return <Circle cx={x(buckets[hoverIdx])} cy={yRev(r.gross)} r={3} fill={revColor} />;
            })()}
            {revenueByLocation.map(s => {
              const r = s.rows.find(rr => rr.bucket === buckets[hoverIdx]);
              if (!r) return null;
              return <Circle key={`hov-rev-${s.locationId}`} cx={x(buckets[hoverIdx])} cy={yRev(r.gross)} r={2.5} fill={s.color} />;
            })}
            {weatherLines.map(wl => {
              const w = wl.byDate[buckets[hoverIdx]];
              if (!w) return null;
              return <Circle key={`hov-${wl.locationId}`} cx={x(buckets[hoverIdx])} cy={yWx(w.severity)} r={2.5} fill={wl.color} />;
            })}
          </>
        )}
        <SvgText x={padL} y={height - 4} fontSize={9} fill={labelColor}>{buckets[0]?.slice(5) ?? ''}</SvgText>
        <SvgText x={width - padR} y={height - 4} fontSize={9} fill={labelColor} textAnchor="end">{buckets[buckets.length - 1]?.slice(5) ?? ''}</SvgText>
      </Svg>
      {hoverIdx != null && tooltipLines.length > 0 && (
        <View style={{ position: 'absolute', top: 0, left: 8, padding: 6, backgroundColor: bg, borderRadius: 4, borderWidth: 1, borderColor: axisColor }}>
          {tooltipLines.map((t, i) => <Text key={i} style={{ fontSize: 11, color: labelColor }}>{t}</Text>)}
        </View>
      )}
      {/* Legend */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <View style={{ width: 10, height: 2, backgroundColor: revColor }} />
          <Text style={{ fontSize: 10, color: labelColor }}>Revenue (All)</Text>
        </View>
        {revenueByLocation.map(s => (
          <View key={`leg-rev-${s.locationId}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 10, height: 2, backgroundColor: s.color, opacity: 0.85 }} />
            <Text style={{ fontSize: 10, color: labelColor }}>{s.label} revenue</Text>
          </View>
        ))}
        {weatherLines.map(wl => (
          <View key={wl.locationId} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 10, height: 2, backgroundColor: wl.color, opacity: 0.85 }} />
            <Text style={{ fontSize: 10, color: labelColor }}>{wl.label} weather</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

// ───────── Heatmap with tooltip ─────────

interface HeatmapProps { cells: HeatmapCell[]; accent: string; bg: string; labelColor: string; }
function Heatmap({ cells, accent, bg, labelColor }: HeatmapProps) {
  const maxGross = Math.max(1, ...cells.map(c => c.gross));
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const [hover, setHover] = useState<{ dow: number; hour: number; gross: number; count: number } | null>(null);

  // Compact hour labels ("6a", "12p") absolutely positioned over the grid so
  // they can overflow their hour column instead of ellipsizing to "…" on
  // narrow Android screens (per-column flexed labels only get ~10px each).
  const hourLabel = (h: number) => (h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`);

  return (
    <View>
      <View style={{ flexDirection: 'row', marginBottom: 4 }}>
        <View style={{ width: 32 }} />
        <View style={{ flex: 1, height: 12 }}>
          {[0, 6, 12, 18].map(h => (
            <Text key={h} style={{ position: 'absolute', left: `${(h / 24) * 100}%`, fontSize: 9, color: labelColor }}>
              {hourLabel(h)}
            </Text>
          ))}
          <Text style={{ position: 'absolute', right: 0, fontSize: 9, color: labelColor }}>11p</Text>
        </View>
      </View>
      {days.map((d, dow) => (
        <View key={d} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 1 }}>
          <Text style={{ width: 32, fontSize: 9, color: labelColor }}>{d}</Text>
          <View style={{ flex: 1, flexDirection: 'row', gap: 1 }}>
            {Array.from({ length: 24 }).map((_, hr) => {
              const c = cells.find(x => x.dow === dow && x.hour === hr);
              const intensity = c ? c.gross / maxGross : 0;
              // True zero-to-max gradient: the chart-wide max cell is full
              // accent, $0 is the empty background, everything else scales
              // linearly (small floor keeps tiny values visible).
              const cellColor = intensity === 0 ? bg : applyAlpha(accent, Math.max(0.06, intensity));
              const webHover = Platform.OS === 'web' ? {
                onMouseEnter: () => setHover({ dow, hour: hr, gross: c?.gross ?? 0, count: c?.count ?? 0 }),
                onMouseLeave: () => setHover(null),
              } : {};
              return (
                <TouchableOpacity
                  key={hr}
                  onPress={() => setHover({ dow, hour: hr, gross: c?.gross ?? 0, count: c?.count ?? 0 })}
                  style={{ flex: 1, height: 14, backgroundColor: cellColor, borderRadius: 2 }}
                  {...webHover}
                />
              );
            })}
          </View>
        </View>
      ))}
      {/* Legend: $0 → chart max */}
      <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: spacing.xs, gap: 4 }}>
        <Text style={{ fontSize: 9, color: labelColor }}>$0</Text>
        <View style={{ flexDirection: 'row', width: 80, height: 8, borderRadius: 2, overflow: 'hidden' }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <View key={i} style={{ flex: 1, backgroundColor: i === 0 ? bg : applyAlpha(accent, Math.max(0.06, i / 11)) }} />
          ))}
        </View>
        <Text style={{ fontSize: 9, color: labelColor }}>${Math.round(maxGross / 100).toLocaleString()}</Text>
      </View>
      {hover && (
        <View style={{ marginTop: spacing.xs, padding: 6, backgroundColor: bg, borderRadius: 4, alignSelf: 'flex-start' }}>
          <Text style={{ fontSize: 11, color: labelColor }}>
            {days[hover.dow]} {hover.hour}:00 — ${(hover.gross / 100).toFixed(2)} · {hover.count} wash{hover.count !== 1 ? 'es' : ''}
          </Text>
        </View>
      )}
    </View>
  );
}

// ───────── Sub-components ─────────

function CompareDelta({ delta, prior, colors }: { delta: number | null; prior: string; colors: ColorTheme }) {
  if (delta == null) {
    return <Text style={{ fontSize: fontSize.xs, color: colors.textLight, marginTop: 2 }}>vs prior {prior}</Text>;
  }
  const up = delta >= 0;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2, marginTop: 2 }}>
      <Ionicons name={up ? 'arrow-up' : 'arrow-down'} size={12} color={up ? colors.success : colors.danger} />
      <Text style={{ fontSize: fontSize.xs, color: up ? colors.success : colors.danger, fontWeight: fontWeight.medium }}>
        {up ? '+' : ''}{delta}% · prior {prior}
      </Text>
    </View>
  );
}

/** Friendly names for the payment-method breakdown buckets. Card payments
 *  come through as lowercase Stripe brand strings and are title-cased. */
const PAYMENT_METHOD_LABELS: Record<string, string> = {
  app: 'In-App',
  fleet: 'Fleet',
  membership: 'Membership Purchases',
  bundle: 'Bundle Purchases',
  gift: 'Gift Credit Purchases',
  credit: 'Prepaid Credit Washes',
  comp: 'Comped (owner/staff)',
  cash: 'Cash (counted at the bay)', // 1.4 P22: coin-pulse inputs, not Rinsepay revenue
};
function paymentMethodLabel(method: string): string {
  return PAYMENT_METHOD_LABELS[method] ?? method.charAt(0).toUpperCase() + method.slice(1);
}

function BreakdownRows({ rows, fmt, colors }: { rows: { label: string; gross: number; count: number }[]; fmt: (n: number) => string; colors: ColorTheme }) {
  const total = Math.max(1, rows.reduce((s, r) => s + r.gross, 0));
  if (rows.length === 0) return <Text style={{ color: colors.textLight, fontSize: fontSize.sm, padding: spacing.md, textAlign: 'center' }}>No data</Text>;
  return (
    <View style={{ marginTop: spacing.xs }}>
      {rows.map(r => (
        <View key={r.label} style={{ marginBottom: spacing.sm }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
            <Text style={{ fontSize: fontSize.sm, color: colors.text, fontWeight: fontWeight.medium }}>{r.label}</Text>
            <Text style={{ fontSize: fontSize.sm, color: colors.text }}>{fmt(r.gross)} · {r.count}</Text>
          </View>
          <View style={{ height: 6, borderRadius: 3, backgroundColor: colors.surfaceSecondary, overflow: 'hidden' }}>
            <View style={{ height: '100%', width: `${(r.gross / total) * 100}%`, backgroundColor: colors.primary }} />
          </View>
        </View>
      ))}
    </View>
  );
}

// ───────── Screen ─────────

export default function OwnerDashboardScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const router = useRouter();
  const { isOffline } = useNetwork();

  const [loading, setLoading] = useState(true);
  const [allLocations, setAllLocations] = useState<LocationRow[]>([]);
  const [selectedLocIds, setSelectedLocIds] = useState<Set<string>>(new Set());
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeStatus | null>(null);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [refunding, setRefunding] = useState<string | null>(null);
  const [uncollectedTabs, setUncollectedTabs] = useState(0);
  const [openTabs, setOpenTabs] = useState<{ count: number; totalCents: number }>({ count: 0, totalCents: 0 });
  const [declineNudges, setDeclineNudges] = useState<{ locationId: string; locationName: string; failedTabs: number; totalTabs: number }[]>([]);
  const [confirmRefund, setConfirmRefund] = useState<string | null>(null);
  const [showPayouts, setShowPayouts] = useState(false);
  const [payoutsPage, setPayoutsPage] = useState(1);
  const PAYOUTS_PER_PAGE = 10;
  const [showExports, setShowExports] = useState(false);
  // Round 2 (S06): the owner's manual Advanced actions, one button each.
  const [equipButtons, setEquipButtons] = useState<{ locationId: string; locationName: string; equipmentKind: 'bay' | 'vending'; equipmentId: string; equipmentName: string; actionId: string; name: string }[]>([]);
  const [runningButton, setRunningButton] = useState<string | null>(null);
  const runEquipButton = async (b: { equipmentKind: 'bay' | 'vending'; equipmentId: string; actionId: string; name: string }) => {
    setRunningButton(b.actionId);
    try { await api(`/api/${b.equipmentKind === 'bay' ? 'bays' : 'vending-machines'}/${b.equipmentId}/actions/${b.actionId}/run`, { method: 'POST', body: {} }); toast.show({ type: 'success', title: `${b.name} started` }); }
    catch (err: any) { toast.show({ type: 'error', title: 'Could not run', message: err.message }); }
    finally { setRunningButton(null); }
  };
  const [mrrData, setMrrData] = useState<MrrData | null>(null);

  // Analytics
  const [preset, setPreset] = useState<RangePreset>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [drillTab, setDrillTab] = useState<'payment' | 'package'>('payment');

  const [revenueRows, setRevenueRows] = useState<RevenueRow[]>([]);
  const [revenueByLocation, setRevenueByLocation] = useState<{ locationId: string; rows: RevenueRow[] }[]>([]);
  const [revenueTz, setRevenueTz] = useState<string>('UTC');
  const [revenueFromTs, setRevenueFromTs] = useState<number>(Date.now() - 30 * 86_400_000);
  const [revenueToTs, setRevenueToTs] = useState<number>(Date.now());
  const [compareWoW, setCompareWoW] = useState<CompareData | null>(null);
  const [compareYoY, setCompareYoY] = useState<CompareData | null>(null);
  const [byBay, setByBay] = useState<ByBayRow[]>([]);
  const [byPayment, setByPayment] = useState<{ method: string; gross: number; count: number }[]>([]);
  const [byPackage, setByPackage] = useState<{ packageName: string; gross: number; count: number }[]>([]);
  const [heatmap, setHeatmap] = useState<HeatmapCell[]>([]);
  const [onboarding, setOnboarding] = useState<{ steps: { key: string; label: string; done: boolean; route: string }[]; complete: boolean } | null>(null);
  const [weather, setWeather] = useState<WeatherRow[]>([]);
  const [weatherSnapshots, setWeatherSnapshots] = useState<{
    locationId: string; bucket: string; tempF: number | null;
    precipChancePct: number | null; conditions: string | null; sampleCount: number;
  }[]>([]);
  const [weatherToday, setWeatherToday] = useState<Record<string, WeatherToday['forecast']>>({});
  const [showWeatherOverlay, setShowWeatherOverlay] = useState(true);

  // Sorting for per-bay table
  const [baySort, setBaySort] = useState<{ key: BaySortKey; dir: SortDir }>({ key: 'gross', dir: 'desc' });

  // Transaction list
  const [txSearch, setTxSearch] = useState('');
  const [txStatusFilter, setTxStatusFilter] = useState('');
  const [txTypeFilter, setTxTypeFilter] = useState('');
  const [txExporting, setTxExporting] = useState(false);
  // Range scope for the Exports box. 'current_view' uses the dashboard's
  // preset chips; the others resolve in owner-tz on the backend.
  const [exportPeriod, setExportPeriod] = useState<'current_view' | 'current_month' | 'previous_month' | 'ytd'>('current_view');
  const [txPage, setTxPage] = useState(1);
  const [txLimit, setTxLimit] = useState(10);
  const [txTotal, setTxTotal] = useState(0);
  const txSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reported issues
  const [reports, setReports] = useState<ProblemReport[]>([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsSearch, setReportsSearch] = useState('');
  const [reportsStatus, setReportsStatus] = useState('');
  const [reportDetail, setReportDetail] = useState<ProblemReport | null>(null);
  const [resolvingReport, setResolvingReport] = useState<string | null>(null);
  const [reportRefundConfirm, setReportRefundConfirm] = useState(false);
  const reportsSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const REPORTS_PER_PAGE = 10;

  // Transaction detail
  const [txDetail, setTxDetail] = useState<Transaction | null>(null);
  const [txDetailReports, setTxDetailReports] = useState<ProblemReport[]>([]);

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const currentRange = useMemo(() => rangeForPreset(preset, customFrom, customTo), [preset, customFrom, customTo]);
  const groupBy = useMemo<'day' | 'hour'>(() => preset === 'today' ? 'hour' : 'day', [preset]);

  // When every location is selected (the default state), send NO location
  // filter so owner-wide revenue — bundles and subscription billings, which
  // have no location_id — is included in the totals. A location filter
  // (`location_id IN (...)`) silently drops those NULL-location rows, which is
  // why bundle purchases were missing from dashboard revenue. Only filter once
  // the owner has narrowed to a strict subset of locations.
  const locIdsParam = useMemo(() => {
    const allSelected = allLocations.length > 0 && selectedLocIds.size >= allLocations.length;
    if (allSelected || selectedLocIds.size === 0) return '';
    return Array.from(selectedLocIds).join(',');
  }, [selectedLocIds, allLocations]);
  const locQs = locIdsParam ? `&locationIds=${locIdsParam}` : '';

  // ─── Range-driven analytics fetch ───
  const fetchAnalytics = useCallback(async () => {
    const { from, to } = currentRange;
    // Count failures across the 9 parallel reads so we can surface ONE toast
    // for the whole batch instead of spamming the user — but stay silent when
    // we already know we're offline (the OfflineBanner is visible).
    let failures = 0;
    const track = <T,>(p: Promise<T>, fallback: T): Promise<T> => p.catch(() => { failures++; return fallback; });

    try {
      const [rev, bay, pay, pkg, hm, wx, snaps, wow, yoy] = await Promise.all([
        track(api<{ rows: RevenueRow[]; byLocation?: { locationId: string; rows: RevenueRow[] }[]; from: string; to: string; timezone: string }>(`/api/analytics/revenue?from=${from}&to=${to}&groupBy=${groupBy}&breakdown=location${locQs}`), { rows: [], byLocation: [], from: '', to: '', timezone: 'UTC' }),
        track(api<{ rows: ByBayRow[] }>(`/api/analytics/revenue/by-bay?from=${from}&to=${to}${locQs}`), { rows: [] }),
        track(api<{ rows: { method: string; gross: number; count: number }[] }>(`/api/analytics/revenue/by-payment?from=${from}&to=${to}${locQs}`), { rows: [] }),
        track(api<{ rows: { packageName: string; gross: number; count: number }[] }>(`/api/analytics/revenue/by-package?from=${from}&to=${to}${locQs}`), { rows: [] }),
        track(api<{ cells: HeatmapCell[] }>(`/api/analytics/heatmap?from=${from}&to=${to}${locQs}`), { cells: [] }),
        track(api<{ rows: WeatherRow[] }>(`/api/analytics/weather?from=${from}&to=${to}${locQs}`), { rows: [] }),
        track(api<{ rows: typeof weatherSnapshots }>(`/api/analytics/weather/snapshots?from=${from}&to=${to}&groupBy=${groupBy}${locQs}`), { rows: [] }),
        track(api<CompareData | null>(`/api/analytics/revenue/compare?period=wow`), null),
        track(api<CompareData | null>(`/api/analytics/revenue/compare?period=yoy`), null),
      ]);
      setRevenueRows(rev.rows);
      setRevenueByLocation(rev.byLocation ?? []);
      if (rev.timezone) setRevenueTz(rev.timezone);
      if (rev.from) setRevenueFromTs(new Date(rev.from).getTime());
      if (rev.to) setRevenueToTs(new Date(rev.to).getTime());
      setByBay(bay.rows);
      setByPayment(pay.rows);
      setByPackage(pkg.rows);
      setHeatmap(hm.cells);
      setWeather(wx.rows);
      setWeatherSnapshots(snaps.rows);
      setCompareWoW(wow);
      setCompareYoY(yoy);
      if (failures > 0 && !isOffline) {
        toast.show({
          type: 'warning',
          title: 'Some analytics didn’t load',
          message: failures === 1 ? '1 chart failed to refresh.' : `${failures} charts failed to refresh.`,
        });
      }
    } catch (err: any) {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Analytics failed', message: err?.message ?? 'Unknown error' });
      }
    }
  }, [currentRange, groupBy, locQs, isOffline, toast]);

  // ─── Today's weather per selected location ───
  const fetchWeatherToday = useCallback(async () => {
    const acc: Record<string, WeatherToday['forecast']> = {};
    for (const loc of allLocations) {
      if (!selectedLocIds.has(loc.id)) continue;
      if (loc.latitude == null || loc.longitude == null) continue;
      try {
        const w = await api<WeatherToday>(`/api/analytics/weather/today?locationId=${loc.id}`);
        acc[loc.id] = w.forecast;
      } catch { /* ignore */ }
    }
    setWeatherToday(acc);
  }, [allLocations, selectedLocIds]);

  const fetchTransactions = useCallback(async (p = 1, l = txLimit, s = txSearch, status = txStatusFilter, type = txTypeFilter) => {
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(l) });
      if (s.trim()) params.set('search', s.trim());
      if (status) params.set('status', status);
      if (type) params.set('type', type);
      if (locIdsParam) params.set('locationIds', locIdsParam);
      // Scope to the dashboard's date-range chips (same from/to the analytics use).
      params.set('from', currentRange.from);
      params.set('to', currentRange.to);
      const data = await api<{ transactions: Transaction[]; total: number; page: number; limit: number }>(
        `/api/transactions?${params}`
      );
      setAllTransactions(data.transactions);
      setTxTotal(data.total);
      setTxPage(data.page);
      setTxLimit(data.limit);
    } catch { /* ignore */ }
  }, [locIdsParam, txLimit, txSearch, txStatusFilter, txTypeFilter, currentRange.from, currentRange.to]);

  const handleTxSearch = (text: string) => {
    setTxSearch(text);
    if (txSearchTimer.current) clearTimeout(txSearchTimer.current);
    txSearchTimer.current = setTimeout(() => { fetchTransactions(1, txLimit, text); }, 400);
  };

  const fetchReports = useCallback(async (p = 1, s = reportsSearch, status = reportsStatus) => {
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(REPORTS_PER_PAGE) });
      if (s.trim()) params.set('search', s.trim());
      if (status) params.set('status', status);
      if (locIdsParam) params.set('locationIds', locIdsParam);
      params.set('from', currentRange.from);
      params.set('to', currentRange.to);
      const data = await api<{ reports: ProblemReport[]; total: number; page: number }>(`/api/owner/reports?${params}`);
      setReports(data.reports);
      setReportsTotal(data.total);
      setReportsPage(data.page);
    } catch { /* ignore */ }
  }, [locIdsParam, reportsSearch, reportsStatus, currentRange.from, currentRange.to]);

  const handleReportsSearch = (text: string) => {
    setReportsSearch(text);
    if (reportsSearchTimer.current) clearTimeout(reportsSearchTimer.current);
    reportsSearchTimer.current = setTimeout(() => { fetchReports(1, text); }, 400);
  };

  React.useEffect(() => { setReportRefundConfirm(false); }, [reportDetail]);

  const openTxDetail = async (tx: Transaction) => {
    setTxDetail(tx);
    setTxDetailReports([]);
    // Pull any reported problems filed against this wash.
    try {
      const data = await api<{ reports: ProblemReport[] }>(`/api/owner/reports?transactionId=${tx.id}&limit=50`);
      setTxDetailReports(data.reports || []);
    } catch { /* ignore */ }
  };

  const handleResolveReport = async (r: ProblemReport, status: 'open' | 'resolved') => {
    setResolvingReport(r.id);
    try {
      await api(`/api/owner/reports/${r.id}`, { method: 'PATCH', body: { status } });
      toast.show({ type: 'success', title: status === 'resolved' ? 'Marked resolved' : 'Reopened' });
      setReportDetail(null);
      await fetchReports(reportsPage);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err?.message || 'Could not update report' });
    } finally {
      setResolvingReport(null);
    }
  };

  // Status filter is applied server-side via the `status` query param, so the
  // returned page + total already reflect the filter chip. No client-side
  // post-filter needed — just surface allTransactions as the visible list.
  const filteredTransactions = allTransactions;

  // ─── Exports ───
  const downloadServerFile = async (path: string, filename: string) => {
    try {
      const { downloadFile } = await import('../../src/utils/fileExport');
      const { getToken } = await import('../../src/api/client');
      const apiBase = (process.env.EXPO_PUBLIC_API_URL as string | undefined) || '';
      const absUrl = `${apiBase}${path}`;
      const headers: Record<string, string> = {};
      if (Platform.OS !== 'web') {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      await downloadFile(absUrl, filename, Object.keys(headers).length > 0 ? headers : undefined);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Export failed', message: err?.message ?? 'Unknown error' });
    }
  };

  /** Build the query params + a filename-friendly slug for the selected export period. */
  const exportRangeParams = (): { params: URLSearchParams; slug: string } => {
    const params = new URLSearchParams();
    if (exportPeriod === 'current_view') {
      params.set('start', currentRange.from);
      params.set('end', currentRange.to);
      return { params, slug: `${currentRange.from}_${currentRange.to}` };
    }
    params.set('period', exportPeriod);
    return { params, slug: exportPeriod };
  };

  const handleExportTxCsv = async (format: 'csv' | 'qbo-csv' = 'csv') => {
    setTxExporting(true);
    try {
      const { params, slug } = exportRangeParams();
      params.set('format', format);
      if (txSearch.trim()) params.set('search', txSearch.trim());
      if (locIdsParam) params.set('locationIds', locIdsParam);
      const base = format === 'qbo-csv' ? 'rinsepay-qbo' : 'rinsepay-transactions';
      await downloadServerFile(`/api/transactions?${params}`, `${base}-${slug}.csv`);
    } finally { setTxExporting(false); }
  };

  const handleExportPnlPdf = async () => {
    const { params, slug } = exportRangeParams();
    params.set('format', 'pdf');
    await downloadServerFile(`/api/analytics/pnl?${params}`, `rinsepay-pnl-${slug}.pdf`);
  };

  const handleExportSalesTax = async (format: 'csv' | 'pdf') => {
    const { params, slug } = exportRangeParams();
    params.set('format', format);
    await downloadServerFile(`/api/analytics/sales-tax?${params}`, `rinsepay-sales-tax-${slug}.${format}`);
  };


  // ─── Initial load ───
  const fetchAll = async () => {
    api<{ buttons: typeof equipButtons }>('/api/owner/equipment-buttons').then(r => setEquipButtons(r.buttons ?? [])).catch(() => setEquipButtons([]));
    try {
      const [locs, dash, stripe] = await Promise.all([
        api<LocationRow[]>('/api/locations'),
        api<DashboardData>('/api/analytics/dashboard'),
        api<StripeStatus>('/api/stripe/connect/status').catch(() => ({ onboarded: false, accountId: null })),
      ]);
      setAllLocations(locs);
      // Initialize selection to all locations on first load
      setSelectedLocIds(prev => prev.size === 0 ? new Set(locs.map(l => l.id)) : prev);
      setDashboard(dash);
      setStripeStatus(stripe);

      if (stripe.onboarded) {
        const [bal, pay, mrr] = await Promise.all([
          api<Balance>('/api/stripe/balance').catch(() => null),
          api<Payout[]>('/api/stripe/payouts').catch(() => []),
          api<MrrData>('/api/subscriptions/mrr').catch(() => null),
        ]);
        setBalance(bal);
        setPayouts(pay);
        setMrrData(mrr);
      }
    } catch (err: any) {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => {
    api<{ steps: { key: string; label: string; done: boolean; route: string }[]; complete: boolean }>('/api/owner/onboarding-status')
      .then(d => setOnboarding(d))
      .catch(() => {});
    // Visit tabs (silent background checks — UI only when > 0).
    api<{ rows: { uncollected?: boolean; totalCents: number }[]; declineNudges?: { locationId: string; locationName: string; failedTabs: number; totalTabs: number }[] }>('/api/owner/visit-tabs?status=open')
      .then(d => {
        const uncollected = d.rows.filter(r => r.uncollected);
        const open = d.rows.filter(r => !r.uncollected);
        setUncollectedTabs(uncollected.length);
        setOpenTabs({ count: open.length, totalCents: open.reduce((a, r) => a + r.totalCents, 0) });
        setDeclineNudges(d.declineNudges ?? []);
      })
      .catch(() => {});
  }, []));

  useFocusEffect(useCallback(() => { fetchAll(); }, []));

  // Re-fetch analytics + transactions whenever range or selection changes.
  React.useEffect(() => {
    if (!loading) {
      fetchAnalytics();
      fetchTransactions(1, txLimit, txSearch);
      fetchReports(1, reportsSearch, reportsStatus);
      fetchWeatherToday();
    }
  }, [currentRange.from, currentRange.to, locIdsParam, loading]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Transaction handlers ───
  const getTxLabel = (tx: Transaction) => {
    switch (tx.type) {
      case 'bundle-purchase': return `Bundle: ${tx.tierName || 'Purchase'}`;
      case 'bundle-wash': return 'Bundle Wash';
      case 'subscription-wash': return 'Member Wash';
      case 'subscription': return 'Membership';
      case 'gift-credit': return 'Gift Credit';
      case 'vacuum': return 'Vacuum';
      case 'vending': return 'Vending';
      // Bay name (already appended to the row title) says what/where it was.
      case 'fleet-wash': return 'Fleet';
      default: return tx.tierName || tx.type;
    }
  };

  const getTxIcon = (tx: Transaction): { name: string; color: string; bg: string } => {
    // Visit Billing: item delivered, charge pending until the visit closes.
    if (tx.status === 'tab_pending') return { name: 'hourglass', color: colors.warning, bg: colors.warning + '15' };
    if (tx.status === 'refunded') return { name: 'return-down-back', color: colors.textLight, bg: colors.textLight + '20' };
    if (tx.status === 'partially_refunded') return { name: 'return-down-back', color: colors.warning, bg: colors.warning + '20' };
    if (tx.type === 'bundle-purchase') return { name: 'cube', color: colors.primary, bg: colors.primary + '15' };
    if (tx.type === 'bundle-wash') return { name: 'cube-outline', color: colors.success, bg: colors.success + '15' };
    if (tx.type === 'subscription-wash') return { name: 'ribbon', color: colors.success, bg: colors.success + '15' };
    if (tx.type === 'subscription') return { name: 'ribbon', color: colors.primary, bg: colors.primary + '15' };
    if (tx.type === 'gift-credit') return { name: 'gift', color: colors.primary, bg: colors.primary + '15' };
    if (tx.type === 'vacuum') return { name: 'sparkles', color: colors.primary, bg: colors.primary + '15' };
    if (tx.type === 'vending') return { name: 'fast-food', color: colors.primary, bg: colors.primary + '15' };
    if (tx.type === 'fleet-wash') return { name: 'business', color: colors.primary, bg: colors.primary + '15' };
    if (tx.status === 'completed') return { name: 'checkmark', color: colors.success, bg: colors.success + '15' };
    if (tx.status === 'owner_activated') return { name: 'person-circle', color: colors.primary, bg: colors.primary + '15' };
    if (tx.status === 'attendant_activated') return { name: 'people-circle', color: colors.primary, bg: colors.primary + '15' };
    return { name: 'time', color: colors.warning, bg: colors.warning + '15' };
  };

  const isSessionRow = (tx: Transaction) => tx.type === 'self-serve' || tx.type === 'vacuum';
  const minutesLabel = (m: number | null | undefined) => `${Math.max(0, Math.round(m ?? 0))} min`;

  const getRefundMessage = (tx: Transaction) => {
    if (tx.type === 'bundle-wash') return 'Restore 1 wash back to the customer\'s bundle?';
    if (tx.type === 'subscription-wash') {
      return tx.bayType === 'self_serve' || tx.bayType === 'vacuum'
        ? `Restore ${minutesLabel(tx.durationMinutes)} back to the customer's membership limits?`
        : 'Restore 1 wash back to the customer\'s daily/monthly limits?';
    }
    if (isSessionRow(tx) && tx.paymentMethod === 'bundle') return `Restore ${minutesLabel(tx.durationMinutes)} back to the customer's bundle?`;
    if (isSessionRow(tx) && tx.amount === 0) return 'Nothing was charged to the card. Give the applied wash credit / promo back to the customer?';
    if (tx.type === 'fleet-wash') return `Refund this fleet wash? The remaining amount is what gets invoiced to ${tx.fleetCompanyName || 'the fleet'}.`;
    return `Refund to customer?`;
  };

  /** Credit-funded rows restore a wash / minutes / credit (no $), so they're full-only. */
  const isRestoreWash = (tx: Transaction) =>
    tx.type === 'bundle-wash' || tx.type === 'subscription-wash'
    || (isSessionRow(tx) && (tx.paymentMethod === 'bundle' || tx.amount === 0));

  const handleRefund = async (tx: Transaction, amount?: number) => {
    setRefunding(tx.id);
    try {
      if (tx.type === 'fleet-wash') {
        // Fleet washes refund against fleet_transactions directly (no Stripe).
        // Partial amounts are off the retail price; the remainder still gets
        // invoiced at the volume discount.
        await api(`/api/fleet/accounts/${tx.fleetAccountId}/transactions/${tx.id}/refund`, {
          method: 'POST', body: amount != null ? { amount } : {},
        });
      } else {
        const body: any = { reason: 'Owner initiated refund' };
        if (tx.type === 'bundle-wash') body.bundleWashId = tx.id;
        else if (tx.type === 'subscription-wash') body.subscriptionWashId = tx.id;
        else if (tx.type === 'bundle-purchase') body.purchasedBundleId = tx.id;
        else if (tx.type === 'self-serve' || tx.type === 'vacuum') body.washSessionId = tx.id;
        else body.transactionId = tx.id; // regular + subscription (Stripe refund)
        if (amount != null && !isRestoreWash(tx)) body.amount = amount;
        await api('/api/refunds', { method: 'POST', body });
      }
      toast.show({ type: 'success', title: isRestoreWash(tx) ? 'Restored to the customer' : 'Refund processed' });
      // fetchAll() reloads locations / analytics / Stripe but NOT the transaction list,
      // so the row stays stale (button keeps appearing, dialog keeps offering the original amount).
      // Refetching the visible page picks up the new refunded_amount + status.
      fetchAll();
      fetchTransactions(txPage, txLimit, txSearch);
      return true;
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Refund failed', message: err.message });
      return false;
    } finally { setRefunding(null); }
  };

  // Refund straight from a reported issue — owners shouldn't have to hunt for the
  // wash in the transactions list. Builds a minimal tx from the report's snapshot.
  const reportToTx = (r: ProblemReport): Transaction => ({
    id: r.transactionId, type: r.txType || 'automatic', tierName: null, durationMinutes: null,
    amount: r.txAmountCents ?? 0, refundedAmount: 0, platformFee: 0, ownerPayout: 0, status: 'completed',
    stripePaymentIntentId: null, createdAt: r.createdAt,
    locationName: r.locationName, bayName: r.bayName, customerName: r.reporterName,
    customerEmail: r.reporterEmail, customerPhone: r.reporterPhone,
    paymentBrand: null, paymentLast4: null, fleetCompanyName: null, rating: null,
  });

  const handleRefundFromReport = async (r: ProblemReport, amount: number) => {
    setReportRefundConfirm(false);
    const ok = await handleRefund(reportToTx(r), amount);
    if (ok) {
      const totalRefunded = (r.refundedAmountCents ?? 0) + amount;
      try {
        await api(`/api/owner/reports/${r.id}`, { method: 'PATCH', body: { status: 'resolved', refundedAmountCents: totalRefunded } });
      } catch { /* refund already succeeded; status update is best-effort */ }
      await fetchReports(reportsPage);
    }
    setReportDetail(null);
  };

  const openStripeDashboard = async () => {
    try {
      const res = await api<{ url: string }>('/api/stripe/connect/dashboard-link', { method: 'POST', body: {} });
      await openExternal(res.url);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleStripeOnboard = async () => {
    try {
      const res = await api<{ url: string }>('/api/stripe/connect/onboard', { method: 'POST', body: {} });
      await openExternal(res.url);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  // Hero metrics
  const rangeTotal = useMemo(() => revenueRows.reduce((s, r) => s + r.gross, 0), [revenueRows]);
  const rangeCount = useMemo(() => revenueRows.reduce((s, r) => s + r.count, 0), [revenueRows]);

  // Per-location revenue series for the trend chart. Skip when only one
  // location is selected — the "All" line already represents it.
  const revenueSeries = useMemo(() => {
    const selectedLocList = allLocations.filter(l => selectedLocIds.has(l.id));
    if (selectedLocList.length < 2) return [];
    return selectedLocList.map((loc, i) => ({
      locationId: loc.id,
      label: loc.name,
      color: LOC_COLORS[i % LOC_COLORS.length],
      rows: revenueByLocation.find(s => s.locationId === loc.id)?.rows ?? [],
    }));
  }, [revenueByLocation, allLocations, selectedLocIds]);

  // Build per-location weather lines from the flat weather rows.
  const weatherLines = useMemo<WeatherLine[]>(() => {
    if (!showWeatherOverlay) return [];
    const selectedLocList = allLocations.filter(l => selectedLocIds.has(l.id));

    // Primary source: weather snapshots taken at the moment of each wash.
    // Fallbacks (used only for buckets with no wash):
    //   - groupBy='hour' → today's hourly NWS forecast
    //   - groupBy='day'  → daily_weather_observations from the 04:00 cron
    return selectedLocList.map((loc, i) => {
      const byDate: Record<string, { severity: number; conditions: string | null; precipInches: number }> = {};

      // Step 1: layer in the fallbacks first (lower priority).
      if (groupBy === 'hour') {
        const periods = weatherToday[loc.id]?.properties?.periods ?? [];
        for (const p of periods) {
          const bucket = formatBucket(new Date(p.startTime), revenueTz, 'hour');
          const conds = p.shortForecast ?? null;
          const precipProb = p.probabilityOfPrecipitation?.value ?? 0;
          byDate[bucket] = {
            severity: weatherSeverity(conds, precipProb / 100),
            conditions: conds,
            precipInches: precipProb / 100,
          };
        }
      } else {
        for (const w of weather) {
          if (w.locationId !== loc.id) continue;
          byDate[w.date] = {
            severity: weatherSeverity(w.conditions, w.precipInches),
            conditions: w.conditions,
            precipInches: w.precipInches,
          };
        }
      }

      // Step 2: overwrite with per-wash snapshots when present.
      for (const s of weatherSnapshots) {
        if (s.locationId !== loc.id) continue;
        byDate[s.bucket] = {
          severity: weatherSeverity(s.conditions, (s.precipChancePct ?? 0) / 100),
          conditions: s.conditions,
          precipInches: (s.precipChancePct ?? 0) / 100,
        };
      }

      return {
        locationId: loc.id,
        label: loc.name,
        color: LOC_COLORS[i % LOC_COLORS.length],
        byDate,
      };
    }).filter(l => Object.keys(l.byDate).length > 0);
  }, [weather, weatherToday, weatherSnapshots, groupBy, revenueTz, showWeatherOverlay, selectedLocIds, allLocations]);

  // Sorted bay rows
  const sortedByBay = useMemo(() => {
    const cmp = (a: ByBayRow, b: ByBayRow): number => {
      const av = a[baySort.key], bv = b[baySort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv);
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv));
    };
    return [...byBay].sort((a, b) => baySort.dir === 'asc' ? cmp(a, b) : -cmp(a, b));
  }, [byBay, baySort]);

  const toggleBaySort = (key: BaySortKey) => {
    setBaySort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'bayName' || key === 'locationName' ? 'asc' : 'desc' });
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        {/* footer=false: transient loading state — no page footer. */}
        <ContentContainer maxWidth={720} footer={false}>
          <Text style={styles.loadingText}>Loading dashboard...</Text>
        </ContentContainer>
      </View>
    );
  }

  if (!stripeStatus?.onboarded) {
    return (
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer maxWidth={720}>
          <Card style={styles.setupCard}>
            <Ionicons name="card-outline" size={48} color={colors.primary} />
            <Text style={styles.setupTitle}>Set Up Payments</Text>
            <Text style={styles.setupDesc}>Connect your Stripe account to start accepting payments and receiving payouts.</Text>
            <Button title="Connect Stripe" onPress={handleStripeOnboard} size="lg" style={{ marginTop: spacing.md }} />
          </Card>
        </ContentContainer>
      </ScrollView>
    );
  }

  const presets: { key: RangePreset; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: '7d', label: '7d' },
    { key: '30d', label: '30d' },
    { key: 'mtd', label: 'MTD' },
    { key: 'lastMonth', label: 'Last mo.' },
    { key: 'ytd', label: 'YTD' },
    { key: 'custom', label: 'Custom' },
  ];

  const toggleLoc = (id: string) => {
    setSelectedLocIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { if (next.size > 1) next.delete(id); }
      else next.add(id);
      return next;
    });
  };
  const allSelected = allLocations.length > 0 && selectedLocIds.size === allLocations.length;
  const toggleAllLocs = () => {
    if (allSelected) {
      // Keep at least one selected
      const first = allLocations[0]?.id;
      setSelectedLocIds(first ? new Set([first]) : new Set());
    } else {
      setSelectedLocIds(new Set(allLocations.map(l => l.id)));
    }
  };

  // Render today's weather (first selected location with data)
  const firstWeatherLoc = allLocations.find(l => selectedLocIds.has(l.id) && weatherToday[l.id]);
  const todayPeriod = firstWeatherLoc ? weatherToday[firstWeatherLoc.id]?.properties?.periods?.[0] : null;

  const BayHeader = ({ k, label, flex = 1, align = 'left' }: { k: BaySortKey; label: string; flex?: number; align?: 'left' | 'right' }) => (
    <TouchableOpacity onPress={() => toggleBaySort(k)} style={{ flex, flexDirection: 'row', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: 2 }}>
      <Text style={[styles.colHeader, align === 'right' && { textAlign: 'right' }]}>{label}</Text>
      {baySort.key === k && (
        <Ionicons name={baySort.dir === 'asc' ? 'arrow-up' : 'arrow-down'} size={11} color={colors.primary} />
      )}
    </TouchableOpacity>
  );

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer maxWidth={720}>

      {/* ── Uncollected visit tabs — combined-visit charges that declined ── */}
      {uncollectedTabs > 0 && (
        <TouchableOpacity onPress={() => router.push('/owner/visit-tabs' as never)}>
          <Card style={{ marginBottom: spacing.md, borderColor: colors.danger, borderWidth: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <Ionicons name="warning" size={20} color={colors.danger} />
              <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.text }}>
                {uncollectedTabs} visit charge{uncollectedTabs === 1 ? '' : 's'} failed to collect — tap to review and retry.
              </Text>
              <Ionicons name="chevron-forward" size={18} color={colors.textLight} />
            </View>
          </Card>
        </TouchableOpacity>
      )}

      {/* ── Open visit tabs — Visit Billing money not yet charged. Revenue
          cards count collected money only; this line explains the gap. ── */}
      {openTabs.count > 0 && (
        <TouchableOpacity onPress={() => router.push('/owner/visit-tabs' as never)}>
          <Card style={{ marginBottom: spacing.md }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <Ionicons name="hourglass" size={20} color={colors.warning} />
              <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.text }}>
                {openTabs.count} open visit{openTabs.count === 1 ? '' : 's'} · {'$' + (openTabs.totalCents / 100).toFixed(2)} pending — counted in revenue when the visit is charged.
              </Text>
              <Ionicons name="chevron-forward" size={18} color={colors.textLight} />
            </View>
          </Card>
        </TouchableOpacity>
      )}

      {/* ── Decline-rate nudge: charge-later locations failing collections —
          suggest Pre-authorize mode (no emails by directive). ── */}
      {declineNudges.map(n => (
        <TouchableOpacity key={n.locationId} onPress={() => router.push(`/owner/location/edit?locationId=${n.locationId}` as never)}>
          <Card style={{ marginBottom: spacing.md, borderColor: colors.warning, borderWidth: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <Ionicons name="card" size={20} color={colors.warning} />
              <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.text }}>
                {n.failedTabs} of {n.totalTabs} combined visits at {n.locationName} failed to collect on the first try this month — consider switching Visit Billing to Pre-authorize.
              </Text>
              <Ionicons name="chevron-forward" size={18} color={colors.textLight} />
            </View>
          </Card>
        </TouchableOpacity>
      ))}

      {/* ── Onboarding checklist — shown until every setup step is done ── */}
      {onboarding && !onboarding.complete && (
        <Card style={{ marginBottom: spacing.md }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm }}>
            <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: colors.text }}>
              Finish setting up
            </Text>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.bold as any, color: colors.primary }}>
              {onboarding.steps.filter(st => st.done).length}/{onboarding.steps.length}
            </Text>
          </View>
          {onboarding.steps.map(st => (
            <TouchableOpacity
              key={st.key}
              disabled={st.done}
              onPress={() => router.push(st.route as never)}
              style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, gap: spacing.sm }}
            >
              <Ionicons
                name={st.done ? 'checkmark-circle' : 'ellipse-outline'}
                size={20}
                color={st.done ? colors.success : colors.textLight}
              />
              <Text style={{
                flex: 1, fontSize: fontSize.sm,
                color: st.done ? colors.textLight : colors.text,
                textDecorationLine: st.done ? 'line-through' : 'none',
              }}>{st.label}</Text>
              {!st.done && <Ionicons name="chevron-forward" size={16} color={colors.primary} />}
            </TouchableOpacity>
          ))}
        </Card>
      )}

      {/* ── Equipment health (a down bay = lost revenue; visible on open) ── */}
      <HardwareHealthCard />

      {/* ── Available Balance (with Payouts dropdown inside) ── */}
      {balance && (
        <Card style={styles.balanceCard}>
          <Text style={styles.balanceLabel}>Available Balance</Text>
          <Text style={styles.balanceAmount}>{balance.available.length > 0 ? fmt(balance.available[0].amount) : '$0.00'}</Text>
          {balance.pending.length > 0 && balance.pending[0].amount > 0 && (
            <Text style={styles.pendingText}>{fmt(balance.pending[0].amount)} pending</Text>
          )}
          <TouchableOpacity onPress={() => setShowPayouts(!showPayouts)} style={styles.payoutsToggle}>
            <Text style={styles.payoutsToggleText}>Payouts</Text>
            <Ionicons name={showPayouts ? 'chevron-up' : 'chevron-down'} size={16} color={'rgba(255,255,255,0.85)'} />
          </TouchableOpacity>
          {showPayouts && (() => {
            const totalPages = Math.max(1, Math.ceil(payouts.length / PAYOUTS_PER_PAGE));
            const page = Math.min(payoutsPage, totalPages);
            const start = (page - 1) * PAYOUTS_PER_PAGE;
            const slice = payouts.slice(start, start + PAYOUTS_PER_PAGE);
            return (
              <View style={{ width: '100%', marginTop: spacing.sm }}>
                {payouts.length > 0 ? (
                  <>
                    {slice.map(p => (
                      <View key={p.id} style={styles.payoutRowInverse}>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.payoutAmountInverse}>{fmt(p.amount)}</Text>
                          <Text style={styles.payoutDateInverse}>{formatDateTime(new Date(p.arrivalDate * 1000).toISOString())} · {p.status}</Text>
                        </View>
                      </View>
                    ))}
                    {totalPages > 1 && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: spacing.sm }}>
                        <TouchableOpacity
                          onPress={() => setPayoutsPage(p => Math.max(1, p - 1))}
                          disabled={page <= 1}
                          style={[styles.payoutsToggle, page <= 1 && { opacity: 0.4 }]}
                        >
                          <Ionicons name="chevron-back" size={16} color={'rgba(255,255,255,0.85)'} />
                          <Text style={styles.payoutsToggleText}>Prev</Text>
                        </TouchableOpacity>
                        <Text style={[styles.payoutDateInverse, { color: 'rgba(255,255,255,0.85)' }]}>
                          Page {page} of {totalPages}
                        </Text>
                        <TouchableOpacity
                          onPress={() => setPayoutsPage(p => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages}
                          style={[styles.payoutsToggle, page >= totalPages && { opacity: 0.4 }]}
                        >
                          <Text style={styles.payoutsToggleText}>Next</Text>
                          <Ionicons name="chevron-forward" size={16} color={'rgba(255,255,255,0.85)'} />
                        </TouchableOpacity>
                      </View>
                    )}
                  </>
                ) : <Text style={styles.payoutDateInverse}>No payouts yet</Text>}
              </View>
            );
          })()}
        </Card>
      )}

      {/* ── Top bar: Location chips ── */}
      {allLocations.length > 0 && (
        <Card style={[styles.section, { paddingVertical: spacing.sm }]}>
          <View style={styles.rangeChipRow}>
            {allLocations.length > 1 && (
              <TouchableOpacity onPress={toggleAllLocs} style={[styles.txChip, allSelected && styles.txChipActive]}>
                <Text style={[styles.txChipText, allSelected && styles.txChipTextActive]}>All</Text>
              </TouchableOpacity>
            )}
            {allLocations.map(l => {
              const on = selectedLocIds.has(l.id);
              return (
                <TouchableOpacity key={l.id} onPress={() => toggleLoc(l.id)} style={[styles.txChip, on && styles.txChipActive]}>
                  <Text style={[styles.txChipText, on && styles.txChipTextActive]}>{l.name}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </Card>
      )}

      {/* ── Date range picker ── */}
      <Card style={styles.section}>
        <View style={styles.rangeHeader}>
          <Text style={styles.sectionTitle}>{currentRange.label}</Text>
          <Text style={styles.rangeDates}>{currentRange.from} → {currentRange.to}</Text>
        </View>
        <View style={styles.rangeChipRow}>
          {presets.map(p => (
            <TouchableOpacity
              key={p.key}
              onPress={() => {
                if (p.key === 'custom') setShowCustomModal(true);
                else setPreset(p.key);
              }}
              style={[styles.txChip, preset === p.key && styles.txChipActive]}
            >
              <Text style={[styles.txChipText, preset === p.key && styles.txChipTextActive]}>{p.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </Card>

      <Modal visible={showCustomModal} transparent animationType="fade" onRequestClose={() => setShowCustomModal(false)}>
        <View style={styles.modalBg}>
          <Card style={styles.modalCard}>
            <Text style={styles.sectionTitle}>Custom range</Text>
            <View style={{ marginBottom: spacing.sm }}>
              <DateTimePicker value={customFrom} onChange={setCustomFrom} mode="date" label="From" />
            </View>
            <View style={{ marginBottom: spacing.sm }}>
              <DateTimePicker value={customTo} onChange={setCustomTo} mode="date" label="To" />
            </View>
            <View style={{ flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm }}>
              <Button title="Cancel" variant="outline" onPress={() => setShowCustomModal(false)} style={{ flex: 1 }} />
              <Button title="Apply" onPress={() => { setPreset('custom'); setShowCustomModal(false); }} style={{ flex: 1 }} />
            </View>
          </Card>
        </View>
      </Modal>

      {/* ── Hero tiles ── */}
      <View style={styles.statsGrid}>
        <Card style={styles.statCard}>
          <Text style={styles.statLabel}>Revenue in range</Text>
          <Text style={[styles.statAmount, { color: colors.primary }]}>{fmt(rangeTotal)}</Text>
          <Text style={styles.statCount}>{rangeCount} washes</Text>
        </Card>
        <Card style={styles.statCard}>
          <Text style={styles.statLabel}>Today</Text>
          <Text style={styles.statAmount}>{fmt(dashboard?.today.totalCharged ?? 0)}</Text>
          <Text style={styles.statCount}>{dashboard?.today.count ?? 0} washes</Text>
        </Card>
        <Card style={styles.statCard}>
          <Text style={styles.statLabel}>Active now</Text>
          <Text style={styles.statAmount}>{dashboard?.activeSessions ?? 0}</Text>
          <Text style={styles.statCount}>bays in use</Text>
        </Card>
        {/* 1.4 P22: cash counted by coin-pulse inputs (only when the owner has any) */}
        {(byPayment.find(r => r.method === 'cash')?.gross ?? 0) > 0 && (
          <Card style={styles.statCard}>
            <Text style={styles.statLabel}>Cash (counted)</Text>
            <Text style={styles.statAmount}>{fmt(byPayment.find(r => r.method === 'cash')!.gross)}</Text>
            <Text style={styles.statCount}>{byPayment.find(r => r.method === 'cash')!.count} coin pulses</Text>
          </Card>
        )}
        <Card style={styles.statCard}>
          <Text style={styles.statLabel}>Avg rating</Text>
          <Text style={styles.statAmount}>{dashboard?.avgRating != null ? dashboard.avgRating.toFixed(1) : '—'}</Text>
          <Text style={styles.statCount}>{dashboard?.ratingCount ?? 0} ratings</Text>
        </Card>
      </View>

      {/* ── Outstanding fleet receivable (revenue recognized at wash-time at the
             discounted price; this is what's billed-but-not-yet-collected) ── */}
      {(dashboard?.fleetPending?.count ?? 0) > 0 && (
        <Card style={[styles.section, styles.pendingFleet]}>
          <Ionicons name="time-outline" size={20} color={colors.primary} style={{ marginRight: spacing.sm }} />
          <View style={{ flex: 1 }}>
            <Text style={styles.pendingFleetTitle}>
              {dashboard!.fleetPending!.count} fleet wash{dashboard!.fleetPending!.count !== 1 ? 'es' : ''} awaiting payment
            </Text>
            <Text style={styles.pendingFleetSub}>
              {fmt(dashboard!.fleetPending!.amount)} outstanding · included in revenue at the discounted price, not yet collected
            </Text>
          </View>
        </Card>
      )}

      {/* ── Today's weather tile ── */}
      {todayPeriod && (
        <Card style={[styles.section, { flexDirection: 'row', alignItems: 'center', gap: spacing.sm }]}>
          <Ionicons
            name={todayPeriod.shortForecast.toLowerCase().includes('rain') ? 'rainy' :
                  todayPeriod.shortForecast.toLowerCase().includes('snow') ? 'snow' :
                  todayPeriod.shortForecast.toLowerCase().includes('cloud') ? 'cloudy' : 'sunny'}
            size={28} color={colors.primary}
          />
          <View style={{ flex: 1 }}>
            <Text style={styles.sectionTitle}>
              {todayPeriod.temperature}°{todayPeriod.temperatureUnit} · {todayPeriod.shortForecast}
            </Text>
            <Text style={styles.legendLabel}>
              {firstWeatherLoc?.name}
              {(todayPeriod.probabilityOfPrecipitation?.value ?? 0) > 0 && ` · ${todayPeriod.probabilityOfPrecipitation?.value}% precip`}
            </Text>
          </View>
        </Card>
      )}

      {/* ── Comparison bars ── */}
      {(compareWoW || compareYoY) && (
        <View style={styles.statsGrid}>
          {compareWoW && (
            <Card style={styles.statCard}>
              <Text style={styles.statLabel}>This week vs last</Text>
              <Text style={styles.statAmount}>{fmt(compareWoW.current.gross)}</Text>
              <CompareDelta delta={compareWoW.deltaPct.gross} colors={colors} prior={fmt(compareWoW.prior.gross)} />
            </Card>
          )}
          {compareYoY && (
            <Card style={styles.statCard}>
              <Text style={styles.statLabel}>MTD vs same MTD last year</Text>
              <Text style={styles.statAmount}>{fmt(compareYoY.current.gross)}</Text>
              <CompareDelta delta={compareYoY.deltaPct.gross} colors={colors} prior={fmt(compareYoY.prior.gross)} />
            </Card>
          )}
        </View>
      )}

      {/* ── Revenue chart with options ── */}
      <Card style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Revenue trend</Text>
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.xs }}>
          <TouchableOpacity onPress={() => setShowWeatherOverlay(!showWeatherOverlay)} style={[styles.txChip, showWeatherOverlay && styles.txChipActive]}>
            <Text style={[styles.txChipText, showWeatherOverlay && styles.txChipTextActive]}>Show weather lines</Text>
          </TouchableOpacity>
        </View>
        <Text style={styles.legendLabel}>One bold "All" revenue line plus a thinner line per selected site. One weather line per site — bottom of band = clear/sunny, top = thunderstorm.</Text>
        <RevenueLineChart
          revenue={revenueRows}
          revenueByLocation={revenueSeries}
          weatherLines={weatherLines}
          fromTs={revenueFromTs}
          toTs={revenueToTs}
          granularity={groupBy === 'hour' ? 'hour' : 'day'}
          timezone={revenueTz}
          axisColor={colors.border}
          labelColor={colors.textLight}
          bg={colors.surface}
          revColor={colors.primary}
        />
      </Card>

      {/* ── Heatmap ── */}
      <Card style={styles.section}>
        <Text style={styles.sectionTitle}>Day-of-week × hour-of-day</Text>
        <Text style={styles.legendLabel}>Hover/tap a cell to see exact revenue.</Text>
        <View style={{ marginTop: spacing.sm }}>
          <Heatmap cells={heatmap} accent={colors.primary} bg={colors.surfaceSecondary} labelColor={colors.textLight} />
        </View>
      </Card>

      {/* ── Per-bay sortable table ── */}
      {byBay.length > 0 && (
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Revenue by bay</Text>
          <View style={styles.tableHeader}>
            <BayHeader k="bayName" label="Bay" flex={2} />
            <BayHeader k="count" label="Washes" align="right" />
            <BayHeader k="gross" label="Gross" align="right" />
            <BayHeader k="avgTicket" label="Avg" align="right" />
          </View>
          {sortedByBay.map(b => (
            <View key={b.bayId} style={styles.tableRow}>
              <View style={{ flex: 2 }}>
                <Text style={styles.tableCell}>{b.bayName}</Text>
                <Text style={styles.tableSub}>{b.locationName} · {b.bayType}</Text>
              </View>
              <Text style={[styles.tableCell, styles.tableNum]}>{b.count}</Text>
              <Text style={[styles.tableCell, styles.tableNum]}>{fmt(b.gross)}</Text>
              <Text style={[styles.tableCell, styles.tableNum]}>{fmt(b.avgTicket)}</Text>
            </View>
          ))}
        </Card>
      )}

      {/* ── Drill-down tabs (payment + package only — location handled by top bar) ── */}
      <Card style={styles.section}>
        <View style={styles.tabRow}>
          {(['payment', 'package'] as const).map(k => (
            <TouchableOpacity key={k} onPress={() => setDrillTab(k)} style={[styles.tabBtn, drillTab === k && styles.tabBtnActive]}>
              <Text style={[styles.tabBtnText, drillTab === k && styles.tabBtnTextActive]}>
                {k === 'payment' ? 'Payment method' : 'Package'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {drillTab === 'payment' && <BreakdownRows rows={byPayment.map(r => ({ label: paymentMethodLabel(r.method), gross: r.gross, count: r.count }))} fmt={fmt} colors={colors} />}
        {drillTab === 'package' && <BreakdownRows rows={byPackage.map(r => ({ label: r.packageName, gross: r.gross, count: r.count }))} fmt={fmt} colors={colors} />}
      </Card>

      {/* ── MRR ── */}
      {mrrData && mrrData.activeCount > 0 && (
        <Card style={styles.section}>
          <View style={styles.sectionHeader}><Text style={styles.sectionTitle}>Memberships</Text></View>
          <View style={styles.mrrGrid}>
            <View style={styles.mrrCard}><Text style={styles.statLabel}>MRR</Text><Text style={styles.mrrAmount}>{fmt(mrrData.mrr)}</Text></View>
            <View style={styles.mrrCard}><Text style={styles.statLabel}>Active Members</Text><Text style={styles.mrrAmount}>{mrrData.activeCount}</Text></View>
            <View style={styles.mrrCard}><Text style={styles.statLabel}>Rev/Member</Text><Text style={styles.mrrAmount}>{mrrData.activeCount > 0 ? fmt(Math.round(mrrData.mrr / mrrData.activeCount)) : '—'}</Text></View>
            <View style={styles.mrrCard}>
              <Text style={styles.statLabel}>Member Washes</Text>
              <Text style={styles.mrrAmount}>{mrrData.washesThisMonth}</Text>
              <Text style={{ fontSize: 10, color: colors.textLight, marginTop: 1 }}>
                {mrrData.automaticWashesThisMonth ?? 0} auto · {mrrData.selfServeSessionsThisMonth ?? 0} self-serve
              </Text>
            </View>
          </View>
        </Card>
      )}

      {/* ── Transactions ──
          Render the card when there's anything to show OR when the user is
          actively searching/filtering — otherwise an empty search would make
          the whole panel vanish and trap the search box. */}
      {(allTransactions.length > 0 || txSearch.length > 0 || txStatusFilter.length > 0 || txTypeFilter.length > 0) && (
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Transactions</Text>
          <View style={styles.txToolbar}>
            <View style={styles.txSearchWrap}>
              <Ionicons name="search" size={16} color={colors.textLight} />
              <TextInput
                style={styles.txSearchInput}
                placeholder="Search transactions..."
                placeholderTextColor={colors.textLight}
                value={txSearch}
                onChangeText={handleTxSearch}
                returnKeyType="search"
                maxLength={100}
              />
              {txSearch ? (
                <TouchableOpacity onPress={() => { setTxSearch(''); fetchTransactions(1, txLimit, ''); }} hitSlop={8}>
                  <Ionicons name="close-circle" size={16} color={colors.textLight} />
                </TouchableOpacity>
              ) : null}
            </View>
            <TouchableOpacity onPress={() => handleExportTxCsv('csv')} style={styles.txCsvBtn} disabled={txExporting}>
              <Ionicons name="download-outline" size={16} color={colors.white} />
              <Text style={styles.txCsvText}>{txExporting ? '...' : 'CSV'}</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.txFilterRow}>
            {[
              { label: 'All', value: '' },
              { label: 'Completed', value: 'completed' },
              { label: 'Refunded', value: 'refunded,partially_refunded' },
              { label: 'Attendant', value: 'owner_activated,attendant_activated,pending_payment' },
            ].map(f => (
              <TouchableOpacity
                key={f.value}
                onPress={() => { setTxStatusFilter(f.value); fetchTransactions(1, txLimit, txSearch, f.value); }}
                style={[styles.txChip, txStatusFilter === f.value && styles.txChipActive]}
              >
                <Text style={[styles.txChipText, txStatusFilter === f.value && styles.txChipTextActive]}>{f.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={styles.txFilterRow}>
            {[
              { label: 'All types', value: '' },
              { label: 'Washes', value: 'automatic,self-serve,bundle-wash,subscription-wash' },
              { label: 'Vacuum', value: 'vacuum' },
              { label: 'Vending', value: 'vending' },
              { label: 'Memberships', value: 'subscription' },
              { label: 'Bundles', value: 'bundle-purchase' },
              { label: 'Gifts', value: 'gift-credit' },
              { label: 'Fleet', value: 'fleet-wash' },
            ].map(f => (
              <TouchableOpacity
                key={f.value || 'all'}
                onPress={() => { setTxTypeFilter(f.value); fetchTransactions(1, txLimit, txSearch, txStatusFilter, f.value); }}
                style={[styles.txChip, txTypeFilter === f.value && styles.txChipActiveType]}
              >
                <Text style={[styles.txChipText, txTypeFilter === f.value && styles.txChipTextActive]}>{f.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <Text style={styles.txCount}>{txTotal} transaction{txTotal !== 1 ? 's' : ''}</Text>

          {filteredTransactions.length === 0 && (
            <Text style={styles.txEmptyMsg}>
              {txSearch || txStatusFilter || txTypeFilter
                ? 'No transactions match your search.'
                : 'No transactions yet.'}
            </Text>
          )}

          {filteredTransactions.map(tx => {
            const icon = getTxIcon(tx);
            return (
              <React.Fragment key={tx.id}>
                <TouchableOpacity style={styles.txRow} onPress={() => openTxDetail(tx)} activeOpacity={0.6}>
                  <View style={[styles.txIcon, { backgroundColor: icon.bg }]}>
                    <Ionicons name={icon.name as keyof typeof Ionicons.glyphMap} size={16} color={icon.color} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <Text style={styles.txTitle}>{getTxLabel(tx)}{tx.bayName ? ` · ${tx.bayName}` : ''}</Text>
                      {tx.reported && <Ionicons name="flag" size={12} color={colors.danger} />}
                    </View>
                    <Text style={styles.txDetail}>{tx.locationName} · {tx.fleetCompanyName || titleCase(tx.customerName) || 'Customer'} · {formatDateTime(tx.createdAt)}</Text>
                    {(tx.customerEmail || tx.customerPhone) && (
                      <Text style={styles.txDetail}>
                        {tx.customerEmail && (
                          <Text style={{ color: colors.primary }} onPress={() => tx.customerEmail && Linking.openURL(`mailto:${tx.customerEmail}`)}>{tx.customerEmail}</Text>
                        )}
                        {tx.customerEmail && tx.customerPhone ? ' · ' : ''}
                        {tx.customerPhone && (
                          <Text style={{ color: colors.primary }} onPress={() => tx.customerPhone && Linking.openURL(`tel:${tx.customerPhone}`)}>{formatPhone(tx.customerPhone)}</Text>
                        )}
                      </Text>
                    )}
                    {tx.paymentLast4 && <Text style={styles.txPayment}>{tx.paymentBrand ? tx.paymentBrand.charAt(0).toUpperCase() + tx.paymentBrand.slice(1) : 'Card'} •••• {tx.paymentLast4}</Text>}
                    {tx.rating != null && (
                      <View style={{ flexDirection: 'row', marginTop: 2 }}>
                        {[1, 2, 3, 4, 5].map(s => <Ionicons key={s} name={s <= tx.rating! ? 'star' : 'star-outline'} size={11} color={colors.warning} />)}
                      </View>
                    )}
                  </View>
                  <View style={styles.txRight}>
                    <Text style={[styles.txAmount, tx.status === 'refunded' && { color: colors.textLight, textDecorationLine: 'line-through' }, tx.status === 'tab_pending' && { color: colors.warning }]}>{fmt(tx.amount)}</Text>
                    {tx.type === 'fleet-wash' && (tx.fleetDiscountPercent ?? 0) > 0 && (
                      <Text style={{ fontSize: 11, color: colors.textLight, marginTop: 1 }}>
                        {fmt(tx.ownerPayout)} after {tx.fleetDiscountPercent}% fleet
                      </Text>
                    )}
                    {/* Net take-home under the gross — after BOTH the platform fee
                        and the Stripe processing fee, matching the tx-detail "Net payout". */}
                    {tx.status === 'tab_pending' && (
                      <Text style={{ fontSize: 11, color: colors.warning, marginTop: 1 }}>Visit open — charges at close</Text>
                    )}
                    {tx.type !== 'fleet-wash' && tx.amount > 0 && tx.status !== 'refunded' && tx.status !== 'tab_pending' && (
                      <Text style={{ fontSize: 11, color: colors.textLight, marginTop: 1 }}>
                        Net {fmt(tx.ownerPayout - (tx.stripeFee ?? 0))}
                      </Text>
                    )}
                    {/* Prepaid gift credit applied (already counted as income at purchase). */}
                    {(tx.creditUsedCents ?? 0) > 0 && (
                      <Text style={{ fontSize: 11, color: colors.textLight, marginTop: 1 }}>
                        {fmt(tx.creditUsedCents!)} credit used
                      </Text>
                    )}
                    {(tx.type === 'bundle-wash' || tx.type === 'subscription-wash') && (
                      <Text style={{ fontSize: 11, color: colors.textLight, marginTop: 1 }}>Paid</Text>
                    )}
                    {(tx.status === 'completed' || tx.status === 'partially_refunded') && (tx.stripePaymentIntentId || (tx.type === 'fleet-wash' && !tx.fleetInvoiced) || isRestoreWash(tx)) && (
                      <TouchableOpacity onPress={() => setConfirmRefund(tx.id)} disabled={refunding === tx.id} hitSlop={8}>
                        <Text style={styles.refundBtn}>{refunding === tx.id ? '...' : isRestoreWash(tx) ? 'Restore' : 'Refund'}</Text>
                      </TouchableOpacity>
                    )}
                    {tx.type === 'fleet-wash' && tx.fleetInvoiced && tx.status !== 'refunded' && (
                      <Text style={{ fontSize: 11, color: colors.textLight, marginTop: 1 }}>Invoiced</Text>
                    )}
                    {tx.status === 'refunded' && <Text style={styles.refundedLabel}>Refunded</Text>}
                    {tx.status === 'partially_refunded' && tx.refundedAmount != null && (
                      <Text style={styles.refundedLabel}>{fmt(tx.refundedAmount)} refunded</Text>
                    )}
                  </View>
                </TouchableOpacity>
                {confirmRefund === tx.id && (
                  isRestoreWash(tx) ? (
                    <InlineConfirm
                      message={getRefundMessage(tx)}
                      visible={true}
                      onConfirm={async () => { setConfirmRefund(null); await handleRefund(tx); }}
                      onCancel={() => setConfirmRefund(null)}
                      confirmLabel="Restore"
                      loading={refunding === tx.id}
                      variant="warning"
                    />
                  ) : (
                    <RefundDialog
                      visible={true}
                      originalCents={tx.amount}
                      alreadyRefundedCents={tx.refundedAmount ?? 0}
                      subtitle={getRefundMessage(tx)}
                      loading={refunding === tx.id}
                      onCancel={() => setConfirmRefund(null)}
                      onConfirm={async (amount) => { setConfirmRefund(null); await handleRefund(tx, amount); }}
                    />
                  )
                )}
              </React.Fragment>
            );
          })}
          {txTotal > 10 && (() => {
            const totalPages = Math.max(1, Math.ceil(txTotal / 10));
            return (
              <View style={styles.pageNav}>
                <TouchableOpacity
                  onPress={() => fetchTransactions(txPage - 1, 10, txSearch)}
                  disabled={txPage <= 1}
                  style={[styles.pageBtn, txPage <= 1 && { opacity: 0.4 }]}
                >
                  <Ionicons name="chevron-back" size={16} color={colors.primary} />
                  <Text style={styles.pageBtnText}>Prev</Text>
                </TouchableOpacity>
                <Text style={styles.pageInfo}>Page {txPage} of {totalPages}</Text>
                <TouchableOpacity
                  onPress={() => fetchTransactions(txPage + 1, 10, txSearch)}
                  disabled={txPage >= totalPages}
                  style={[styles.pageBtn, txPage >= totalPages && { opacity: 0.4 }]}
                >
                  <Text style={styles.pageBtnText}>Next</Text>
                  <Ionicons name="chevron-forward" size={16} color={colors.primary} />
                </TouchableOpacity>
              </View>
            );
          })()}
        </Card>
      )}

      {/* ── Reported Issues (always shown so owners know where to find them) ── */}
      <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Reported Issues</Text>
          <View style={styles.txToolbar}>
            <View style={styles.txSearchWrap}>
              <Ionicons name="search" size={16} color={colors.textLight} />
              <TextInput
                style={styles.txSearchInput}
                placeholder="Search reported issues..."
                placeholderTextColor={colors.textLight}
                value={reportsSearch}
                onChangeText={handleReportsSearch}
                returnKeyType="search"
                maxLength={100}
              />
              {reportsSearch ? (
                <TouchableOpacity onPress={() => { setReportsSearch(''); fetchReports(1, ''); }} hitSlop={8}>
                  <Ionicons name="close-circle" size={16} color={colors.textLight} />
                </TouchableOpacity>
              ) : null}
            </View>
          </View>
          <View style={styles.txFilterRow}>
            {[
              { label: 'All', value: '' },
              { label: 'Open', value: 'open' },
              { label: 'Resolved', value: 'resolved' },
            ].map(f => (
              <TouchableOpacity
                key={f.value || 'all'}
                onPress={() => { setReportsStatus(f.value); fetchReports(1, reportsSearch, f.value); }}
                style={[styles.txChip, reportsStatus === f.value && styles.txChipActive]}
              >
                <Text style={[styles.txChipText, reportsStatus === f.value && styles.txChipTextActive]}>{f.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <Text style={styles.txCount}>{reportsTotal} issue{reportsTotal !== 1 ? 's' : ''}</Text>

          {reports.length === 0 && (
            <Text style={styles.txEmptyMsg}>
              {reportsSearch || reportsStatus ? 'No issues match your filter.' : 'No reported issues.'}
            </Text>
          )}

          {reports.map(r => (
            <TouchableOpacity key={r.id} style={styles.txRow} onPress={() => setReportDetail(r)} activeOpacity={0.6}>
              <View style={[styles.txIcon, { backgroundColor: (r.status === 'resolved' ? colors.success : colors.warning) + '20' }]}>
                <Ionicons name={r.status === 'resolved' ? 'checkmark-circle' : 'alert-circle'} size={16} color={r.status === 'resolved' ? colors.success : colors.warning} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.txTitle}>{r.categoryLabel}{r.bayName ? ` · ${r.bayName}` : ''}</Text>
                <Text style={styles.txDetail} numberOfLines={1}>{r.description}</Text>
                <Text style={styles.txDetail}>{r.locationName || '—'} · {titleCase(r.reporterName) || 'Customer'} · {formatDateTime(r.createdAt)}</Text>
              </View>
              <View style={styles.txRight}>
                <Text style={{ fontSize: 11, fontWeight: fontWeight.semibold as any, color: r.status === 'resolved' ? colors.success : colors.warning }}>
                  {r.status === 'resolved' ? 'Resolved' : 'Open'}
                </Text>
                <Ionicons name="chevron-forward" size={16} color={colors.textLight} style={{ marginTop: 2 }} />
              </View>
            </TouchableOpacity>
          ))}
          {reportsTotal > REPORTS_PER_PAGE && (() => {
            const totalPages = Math.max(1, Math.ceil(reportsTotal / REPORTS_PER_PAGE));
            return (
              <View style={styles.pageNav}>
                <TouchableOpacity onPress={() => fetchReports(reportsPage - 1)} disabled={reportsPage <= 1} style={[styles.pageBtn, reportsPage <= 1 && { opacity: 0.4 }]}>
                  <Ionicons name="chevron-back" size={16} color={colors.primary} />
                  <Text style={styles.pageBtnText}>Prev</Text>
                </TouchableOpacity>
                <Text style={styles.pageInfo}>Page {reportsPage} of {totalPages}</Text>
                <TouchableOpacity onPress={() => fetchReports(reportsPage + 1)} disabled={reportsPage >= totalPages} style={[styles.pageBtn, reportsPage >= totalPages && { opacity: 0.4 }]}>
                  <Text style={styles.pageBtnText}>Next</Text>
                  <Ionicons name="chevron-forward" size={16} color={colors.primary} />
                </TouchableOpacity>
              </View>
            );
          })()}
      </Card>

      {/* Reported issue detail */}
      <Modal visible={reportDetail !== null} transparent animationType="fade" onRequestClose={() => setReportDetail(null)}>
        <View style={styles.reportModalBackdrop}>
          <View style={styles.reportModalCard}>
            {reportDetail && (
              <ScrollView>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.md }}>
                  <Text style={styles.sectionTitle}>{reportDetail.categoryLabel}</Text>
                  <TouchableOpacity onPress={() => setReportDetail(null)} hitSlop={8}>
                    <Ionicons name="close" size={24} color={colors.textSecondary} />
                  </TouchableOpacity>
                </View>
                <View style={{ alignSelf: 'flex-start', backgroundColor: (reportDetail.status === 'resolved' ? colors.success : colors.warning) + '20', paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: borderRadius.full, marginBottom: spacing.md }}>
                  <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold as any, color: reportDetail.status === 'resolved' ? colors.success : colors.warning }}>
                    {reportDetail.status === 'resolved' ? 'Resolved' : 'Open'}
                  </Text>
                </View>
                <Text style={styles.reportDetailBody}>{reportDetail.description}</Text>
                <View style={styles.reportDetailMeta}>
                  <Text style={styles.txDetail}>Location: {reportDetail.locationName || '—'}{reportDetail.bayName ? ` · ${reportDetail.bayName}` : ''}</Text>
                  <Text style={styles.txDetail}>Reported: {formatDateTime(reportDetail.createdAt)}</Text>
                  {reportDetail.txType && <Text style={styles.txDetail}>Wash: {reportDetail.txType}{reportDetail.txAmountCents != null ? ` · ${fmt(reportDetail.txAmountCents)}` : ''}</Text>}
                  <Text style={styles.txDetail}>Prefers: {reportDetail.contactPreference}</Text>
                  <Text style={[styles.txDetail, { marginTop: spacing.xs, fontWeight: fontWeight.semibold as any, color: colors.text }]}>{titleCase(reportDetail.reporterName) || 'Customer'}</Text>
                  {reportDetail.reporterEmail && (
                    <Text style={[styles.txDetail, { color: colors.primary }]} onPress={() => Linking.openURL(`mailto:${reportDetail.reporterEmail}`)}>{reportDetail.reporterEmail}</Text>
                  )}
                  {reportDetail.reporterPhone && (
                    <Text style={[styles.txDetail, { color: colors.primary }]} onPress={() => Linking.openURL(`tel:${reportDetail.reporterPhone}`)}>{formatPhone(reportDetail.reporterPhone)}</Text>
                  )}
                </View>
                {(reportDetail.refundedAmountCents ?? 0) > 0 && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.lg }}>
                    <Ionicons name="checkmark-circle" size={18} color={colors.success} />
                    <Text style={{ color: colors.success, fontWeight: fontWeight.semibold as any }}>Refunded {fmt(reportDetail.refundedAmountCents!)}</Text>
                  </View>
                )}
                {(reportDetail.txAmountCents ?? 0) - (reportDetail.refundedAmountCents ?? 0) > 0 && (
                  reportRefundConfirm ? (
                    <RefundDialog
                      visible={true}
                      originalCents={reportDetail.txAmountCents ?? 0}
                      alreadyRefundedCents={reportDetail.refundedAmountCents ?? 0}
                      subtitle={`Refund this wash to ${titleCase(reportDetail.reporterName) || 'the customer'}`}
                      loading={refunding === reportDetail.transactionId}
                      onCancel={() => setReportRefundConfirm(false)}
                      onConfirm={(amount) => { handleRefundFromReport(reportDetail, amount); }}
                    />
                  ) : (
                    <Button
                      title={(reportDetail.refundedAmountCents ?? 0) > 0 ? 'Refund more' : `Refund ${fmt(reportDetail.txAmountCents!)}`}
                      onPress={() => setReportRefundConfirm(true)}
                      variant="outline"
                      icon={<Ionicons name="return-down-back" size={18} color={colors.danger} />}
                      style={{ marginTop: spacing.md }}
                    />
                  )
                )}
                <Button
                  title={reportDetail.status === 'resolved' ? 'Reopen' : 'Mark Resolved'}
                  onPress={() => handleResolveReport(reportDetail, reportDetail.status === 'resolved' ? 'open' : 'resolved')}
                  loading={resolvingReport === reportDetail.id}
                  variant={reportDetail.status === 'resolved' ? 'outline' : 'primary'}
                  style={{ marginTop: spacing.sm }}
                />
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Transaction detail */}
      <Modal visible={txDetail !== null} transparent animationType="fade" onRequestClose={() => setTxDetail(null)}>
        <View style={styles.reportModalBackdrop}>
          <View style={styles.reportModalCard}>
            {txDetail && (
              <ScrollView>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm }}>
                  <Text style={styles.sectionTitle}>{getTxLabel(txDetail)}</Text>
                  <TouchableOpacity onPress={() => setTxDetail(null)} hitSlop={8}>
                    <Ionicons name="close" size={24} color={colors.textSecondary} />
                  </TouchableOpacity>
                </View>
                <Text style={{ fontSize: fontSize.xxl, fontWeight: fontWeight.bold as any, color: colors.text }}>{fmt(txDetail.amount)}</Text>
                <Text style={styles.txDetail}>{txDetail.locationName || '—'}{txDetail.bayName ? ` · ${txDetail.bayName}` : ''} · {formatDateTime(txDetail.createdAt)}</Text>

                <View style={styles.reportDetailMeta}>
                  {txDetail.tierName && <Text style={styles.txDetail}>Tier: {txDetail.tierName}</Text>}
                  {txDetail.durationMinutes != null && <Text style={styles.txDetail}>Duration: {txDetail.durationMinutes} min</Text>}
                  <View style={styles.feeRow}><Text style={styles.txDetail}>Gross</Text><Text style={styles.feeVal}>{fmt(txDetail.amount)}</Text></View>
                  <View style={styles.feeRow}><Text style={styles.txDetail}>Platform fee</Text><Text style={styles.feeVal}>−{fmt(txDetail.platformFee)}</Text></View>
                  {txDetail.stripeFee != null && txDetail.stripeFee > 0 && (
                    <View style={styles.feeRow}><Text style={styles.txDetail}>Stripe fee{txDetail.stripeFeeEstimated ? ' (est.)' : ''}</Text><Text style={styles.feeVal}>−{fmt(txDetail.stripeFee)}</Text></View>
                  )}
                  <View style={styles.feeRow}><Text style={[styles.txDetail, { fontWeight: fontWeight.semibold as any, color: colors.text }]}>Net payout</Text><Text style={[styles.feeVal, { color: colors.text }]}>{fmt(txDetail.ownerPayout - (txDetail.stripeFee ?? 0))}</Text></View>
                  {(txDetail.creditUsedCents ?? 0) > 0 && <Text style={styles.txDetail}>Prepaid credit applied: {fmt(txDetail.creditUsedCents!)} (counted as income at purchase)</Text>}
                  {(txDetail.refundedAmount ?? 0) > 0 && <Text style={[styles.txDetail, { color: colors.warning }]}>Refunded: {fmt(txDetail.refundedAmount!)}</Text>}
                  {txDetail.paymentLast4 && <Text style={styles.txDetail}>{(txDetail.paymentBrand || 'Card')} •••• {txDetail.paymentLast4}</Text>}
                  {txDetail.rating != null && (
                    <View style={{ flexDirection: 'row', marginTop: 2 }}>
                      {[1, 2, 3, 4, 5].map(s => <Ionicons key={s} name={s <= (txDetail.rating || 0) ? 'star' : 'star-outline'} size={13} color={colors.warning} />)}
                    </View>
                  )}
                </View>

                <View style={styles.reportDetailMeta}>
                  <Text style={[styles.txDetail, { fontWeight: fontWeight.semibold as any, color: colors.text }]}>{titleCase(txDetail.customerName) || txDetail.fleetCompanyName || 'Customer'}</Text>
                  {txDetail.customerEmail && <Text style={[styles.txDetail, { color: colors.primary }]} onPress={() => txDetail.customerEmail && Linking.openURL(`mailto:${txDetail.customerEmail}`)}>{txDetail.customerEmail}</Text>}
                  {txDetail.customerPhone && <Text style={[styles.txDetail, { color: colors.primary }]} onPress={() => txDetail.customerPhone && Linking.openURL(`tel:${txDetail.customerPhone}`)}>{formatPhone(txDetail.customerPhone)}</Text>}
                </View>

                {txDetailReports.length > 0 && (
                  <View style={styles.reportDetailMeta}>
                    <Text style={[styles.txDetail, { fontWeight: fontWeight.semibold as any, color: colors.text, marginBottom: spacing.xs }]}>Reported problems ({txDetailReports.length})</Text>
                    {txDetailReports.map(r => (
                      <TouchableOpacity key={r.id} onPress={() => { setTxDetail(null); setReportDetail(r); }} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.xs }}>
                        <Ionicons name={r.status === 'resolved' ? 'checkmark-circle' : 'alert-circle'} size={16} color={r.status === 'resolved' ? colors.success : colors.warning} style={{ marginRight: spacing.xs }} />
                        <Text style={[styles.txDetail, { flex: 1 }]} numberOfLines={1}>{r.categoryLabel} — {r.description}</Text>
                        <Ionicons name="chevron-forward" size={14} color={colors.textLight} />
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* ── Expenses ── */}
      <ExpensesBox />

      {/* ── Exports menu ── */}
      <Card style={styles.section}>
        <TouchableOpacity onPress={() => setShowExports(!showExports)} style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Exports</Text>
          <Ionicons name={showExports ? 'chevron-up' : 'chevron-down'} size={20} color={colors.textSecondary} />
        </TouchableOpacity>
        {showExports && (
          <View style={{ gap: spacing.sm, marginTop: spacing.sm }}>
            <Picker
              label="Date range"
              options={[
                { label: `Current view (${currentRange.from} → ${currentRange.to})`, value: 'current_view' },
                { label: 'Current month', value: 'current_month' },
                { label: 'Previous month', value: 'previous_month' },
                { label: 'Year-to-date', value: 'ytd' },
              ]}
              value={exportPeriod}
              onValueChange={(v) => setExportPeriod(v as typeof exportPeriod)}
            />
            <Button title="Transactions CSV" variant="outline" onPress={() => handleExportTxCsv('csv')} icon={<Ionicons name="document-text-outline" size={16} color={colors.primary} />} />
            <Button title="QuickBooks CSV" variant="outline" onPress={() => handleExportTxCsv('qbo-csv')} icon={<Ionicons name="document-outline" size={16} color={colors.primary} />} />
            <Button title="P&L PDF" variant="outline" onPress={handleExportPnlPdf} icon={<Ionicons name="bar-chart-outline" size={16} color={colors.primary} />} />
            <Button title="Sales Tax Report (PDF)" variant="outline" onPress={() => handleExportSalesTax('pdf')} icon={<Ionicons name="receipt-outline" size={16} color={colors.primary} />} />
            <Button title="Sales Tax Report (CSV)" variant="outline" onPress={() => handleExportSalesTax('csv')} icon={<Ionicons name="receipt-outline" size={16} color={colors.primary} />} />
            <Text style={styles.legendLabel}>All exports use the date range above, resolved in your timezone. Tip: configure sales-tax rates per location in Location → Edit (default 0).</Text>
          </View>
        )}
      </Card>

      {/* ── Round 2 (S06): Equipment buttons ── */}
      {equipButtons.length > 0 && (
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Equipment buttons</Text>
          {[...new Set(equipButtons.map(b => b.locationId))].map(locId => {
            const mine = equipButtons.filter(b => b.locationId === locId);
            return (
              <View key={locId} style={{ marginBottom: spacing.sm }}>
                <Text style={styles.legendLabel}>{mine[0].locationName}</Text>
                {[...new Set(mine.map(b => b.equipmentId))].map(eqId => {
                  const rows = mine.filter(b => b.equipmentId === eqId);
                  return (
                    <View key={eqId} style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xs }}>
                      <Text style={{ fontSize: fontSize.sm, color: colors.text, minWidth: 120 }}>{rows[0].equipmentName}</Text>
                      {rows.map(b => <Button key={b.actionId} title={runningButton === b.actionId ? 'Running…' : b.name} size="sm" variant="outline" disabled={!!runningButton} onPress={() => runEquipButton(b)} icon={<Ionicons name="flash-outline" size={14} color={colors.primary} />} />)}
                    </View>
                  );
                })}
              </View>
            );
          })}
        </Card>
      )}

      {/* ── Customers shortcut ── */}
      <Button
        title="View and/or Import Customers"
        variant="outline"
        onPress={() => router.push('/owner/customers' as never)}
        icon={<Ionicons name="people-outline" size={18} color={colors.primary} />}
        style={styles.stripeDashBtn}
      />

      {/* ── Visit billing (combined-charge visit tabs) ── */}
      <Button
        title="Visit Billing"
        variant="outline"
        onPress={() => router.push('/owner/visit-tabs' as never)}
        icon={<Ionicons name="card-outline" size={18} color={colors.primary} />}
        style={styles.stripeDashBtn}
      />

      <Button title="Open Stripe Dashboard" onPress={openStripeDashboard} variant="outline" icon={<Ionicons name="open-outline" size={18} color={colors.primary} />} style={styles.stripeDashBtn} />

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

// ───── Styles ─────

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { fontSize: fontSize.md, color: c.textSecondary },

  setupCard: { alignItems: 'center', padding: spacing.xl },
  setupTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.md },
  setupDesc: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.sm, maxWidth: 280 },

  balanceCard: { alignItems: 'center', padding: spacing.lg, marginBottom: spacing.md, backgroundColor: c.primaryDark, borderRadius: borderRadius.lg },
  balanceLabel: { fontSize: fontSize.sm, color: 'rgba(255,255,255,0.8)' },
  balanceAmount: { fontSize: fs(36), fontWeight: fontWeight.bold, color: c.white, marginTop: spacing.xs },
  pendingText: { fontSize: fontSize.sm, color: 'rgba(255,255,255,0.6)', marginTop: spacing.xs },
  payoutsToggle: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: spacing.sm, paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: borderRadius.sm, backgroundColor: 'rgba(255,255,255,0.12)' },
  payoutsToggleText: { color: 'rgba(255,255,255,0.85)', fontSize: fontSize.xs, fontWeight: fontWeight.medium },
  payoutRowInverse: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.12)' },
  payoutAmountInverse: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.white },
  payoutDateInverse: { fontSize: fontSize.xs, color: 'rgba(255,255,255,0.7)', marginTop: 2 },

  statsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md },
  // Memberships box: tighter than statCard so all four stats fit two-up on
  // phones instead of stacking into one oversized column.
  mrrGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  mrrCard: { flexGrow: 1, flexBasis: '45%', alignItems: 'center', paddingVertical: spacing.sm, paddingHorizontal: spacing.xs },
  mrrAmount: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginTop: 2 },
  statCard: { flex: 1, minWidth: 140, alignItems: 'center', padding: spacing.md },
  statLabel: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  statAmount: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.xs },
  statCount: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  pendingFleet: { flexDirection: 'row', alignItems: 'center' },
  pendingFleetTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  pendingFleetSub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },

  section: { marginBottom: spacing.md },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },

  rangeHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs },
  rangeDates: { fontSize: fontSize.xs, color: c.textLight },
  rangeChipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs },

  legendLabel: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },

  tableHeader: { flexDirection: 'row', paddingVertical: spacing.xs, borderBottomWidth: 1, borderBottomColor: c.border },
  tableRow: { flexDirection: 'row', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight, alignItems: 'center' },
  tableCell: { flex: 1, fontSize: fontSize.xs, color: c.text },
  tableSub: { fontSize: fontSize.xs - 1, color: c.textLight, marginTop: 2 },
  tableNum: { textAlign: 'right' },
  colHeader: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary },

  tabRow: { flexDirection: 'row', gap: spacing.xs, marginBottom: spacing.sm },
  tabBtn: { flex: 1, paddingVertical: 6, alignItems: 'center', borderRadius: borderRadius.sm, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  tabBtnActive: { backgroundColor: c.primary, borderColor: c.primary },
  tabBtnText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  tabBtnTextActive: { color: c.white },

  txRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  txIcon: { width: rs(30), height: rs(30), borderRadius: rs(15), justifyContent: 'center', alignItems: 'center', marginRight: spacing.sm },
  txTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  txDetail: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  txPayment: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  txRight: { alignItems: 'flex-end' },
  txAmount: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  refundBtn: { fontSize: fontSize.xs, color: c.danger, fontWeight: fontWeight.medium, marginTop: 4 },
  refundedLabel: { fontSize: fontSize.xs, color: c.textLight, marginTop: 4 },

  txToolbar: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm },
  txSearchWrap: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 6 },
  txSearchInput: { flex: 1, fontSize: fontSize.sm, color: c.text, padding: 0 },
  txCsvBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: c.primary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 6, gap: 4 },
  txCsvText: { color: c.white, fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  txFilterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.xs },
  txChip: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  txChipActive: { backgroundColor: c.primary, borderColor: c.primary },
  // Second (type) filter row highlights in brand brown to read separately
  // from the teal status row above it.
  txChipActiveType: { backgroundColor: c.secondary, borderColor: c.secondary },
  txChipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  txChipTextActive: { color: c.white },
  txCount: { fontSize: fontSize.xs, color: c.textLight, marginBottom: spacing.sm },
  txEmptyMsg: { fontSize: fontSize.sm, color: c.textLight, paddingVertical: spacing.lg, textAlign: 'center' },

  reportModalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', padding: spacing.lg },
  reportModalCard: { backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg, maxHeight: '80%' },
  reportDetailBody: { fontSize: fontSize.md, color: c.text, lineHeight: 22, marginBottom: spacing.md },
  reportDetailMeta: { gap: 2, borderTopWidth: 1, borderTopColor: c.borderLight, paddingTop: spacing.md, marginTop: spacing.md },
  feeRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 2 },
  feeVal: { fontSize: fontSize.sm, color: c.textSecondary },

  showMoreBtn: { alignItems: 'center', paddingVertical: spacing.md },
  showMoreText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  pageNav: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.sm },
  pageBtn: { flexDirection: 'row', alignItems: 'center', gap: 2, paddingVertical: spacing.xs, paddingHorizontal: spacing.sm },
  pageBtnText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  pageInfo: { fontSize: fontSize.xs, color: c.textSecondary },

  stripeDashBtn: { marginTop: spacing.sm },

  modalBg: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.4)', padding: spacing.md },
  modalCard: { padding: spacing.lg, width: '100%', maxWidth: 380 },
  formLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: spacing.sm },
  formInput: { borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 8, color: c.text, marginTop: 4 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
